import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import XLSX from 'xlsx';
import { View, Text, Pressable, TextInput } from '../lib/react-native-compat';
// Not db/company's pNum — that one rounds to 2 decimals, which would chop the
// 1.633 wage multiplier down to 1.63 and skew every computed rate.
const pNum = (v) => {
    const n = parseFloat(String(v ?? '').replace(/[$,\s]/g, ''));
    return Number.isFinite(n) ? n : 0;
};
import { showToast } from '../helpers/toast';
import { loadLaborClassifications } from '../db/company';
import { SIGNER_ROLES, SIGNER_DAYS, signerDays } from './billingTicketDocs';
import { FUEL_SETTINGS_DEFAULT, EIA_PADD3_QUARTERLY, adderPct, quarterOf, shiftQuarter, quarterSpan, fmtPct, fuelExcluded } from './billingFuel';
import { PT_SETTINGS_DEFAULT, normPt, ptRuleMatch, isPassThru } from './billingPassThru';
import {
    CONTRACT_SETTINGS_DEFAULT, contractOn, contractRule, contractFor, contractTerms, rulesFor, effectLabel, EFFECT_TONE, RUBRIC, isFreightDs,
} from './billingContract';
import { Chip, OutlineButton } from './procurementFilters';
import {
    DEFAULT_RATE_CONFIG, DEFAULT_SCHEDULE, LABOR_SECTIONS, EQUIP_SECTIONS, UOM_LABELS,
} from './billingRateSchedule';
import LaborClassMap, { parseLaborMap, parseClassCatalog, mapRowId, normClass, suggestVoltaClass, fieldSuggestions } from './billingLaborMap';
import EquipTierMap, { parseEquipMap, equipMapRowId, tierDescr, suggestEquipRow, BID_TIER_CODES, tierBidStatus } from './billingEquipMap';
import RateChangeLog, { logRows, rateSaveEntries, settingsSaveEntries } from './billingRateLog';

// Rates & Rules view for the Billing tab — the job's cost-plus rate schedule
// in the VoltaGrid Block 4 & 5 rate-sheet format: labor rates (wage ×
// multiplier), labor provisions, equipment rates, equipment provisions, and
// the fuel surcharge index. Every row and rate is editable per job; the
// defaults in billingRateSchedule.js seed jobs that have never saved one.
//
// Storage rides the same Forecast_User_Data tab 'Billing' rows the rest of
// the Billing tab uses (saveRows comes in from billing.js):
//   fieldName 'rateset' rowId 'rateset'  Notes = { m, otf } — wage multiplier
//     and the overtime factor applied on top of the ST rate.
//   fieldName 'rate'    rowId 'rt_<hash>' — one row per edited/added rate
//     line. Value = wage (labor) / rate (equipment) / % adder (fuel index),
//     Notes = { t: 'L'|'E'|'F', s: section, n: name, no?: notes, u?: uom,
//     f?: 0|1 (equipment: burns fuel — the fuel surcharge base), del?: 1 }.
//     Rows stored before the fuel flag existed carry no f and keep the
//     schedule default. Default rows only get a stored row once touched; del: 1
//     hides a default row (deleteUserData needs GUIDs, so nothing deletes).
//   fieldName 'rule'    rowId 'ru_<sec>_<n>' — provisions text. Notes =
//     { s: 'labor'|'equip'|'fuel', x: text, del?: 1 }.
//   fieldName 'ratemap' rowId 'rm_<hash>' — JDE labor classification → rate
//     sheet classification. Owned and documented by billingLaborMap.js.
//   fieldName 'eqmap' rowId 'em_<hash>' — JDE equipment tier → rate sheet
//     equipment row id. Owned and documented by billingEquipMap.js.
//   fieldName 'ratelog' rowId 'rl_<...>' — the append-only change log behind
//     the Version history tab. Owned and documented by billingRateLog.js;
//     both save paths here write it alongside the rows they change.
// A stored row with blank Value AND blank Notes is a no-op (that's how Reset
// to defaults "clears" rows it can't delete).

const money = (n) => {
    const v = pNum(n);
    const abs = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return (v < 0 ? '-$' : '$') + abs;
};

// Round half-up in cents with a relative-epsilon nudge — 75 × 1.633 × 1.4 is
// exactly 171.465 in decimal but lands a hair under it in binary floats, and
// plain Math.round would print a penny short of the sheet.
export const round2 = (n) => { const v = pNum(n); return Math.round((v + Number.EPSILON * v) * 100) / 100; };

// ST = wage × multiplier; OT = wage × multiplier × OT factor, rounded once at
// the end — the 2026.08.31 sheet computes its printed OT column from the
// unrounded ST (every one of its 34 distinct OT values matches this and 12
// mismatch rounded-ST × 1.4 by a penny).
export const stRate = (wage, cfg) => round2(pNum(wage) * pNum(cfg.m));
export const otRate = (wage, cfg) => round2(pNum(wage) * pNum(cfg.m) * pNum(cfg.otf));

const RULE_SECTIONS = ['labor', 'equip', 'fuel'];

// Billing settings defaults — stored as fieldName 'settings' rowId 'settings'
// on the same Billing tab rows. The Markup & settings card here owns editing;
// billing.js imports this to merge stored settings and price transactions.
export const DEFAULT_SETTINGS = {
    // Labor markup is set per billable labor cost type (51100/51200/51500);
    // the rest of the classes rate as a bucket. Equipment (owned fleet AND
    // rental) bills hours × the rate sheet tier rate — the Equipment % here
    // is only the fallback for tiers that aren't on the rate sheet. The
    // non-labor buckets all seed at the provisions' 12% administrative and
    // handling fee (materials, third-party services, and equipment not on
    // the rate sheet all bill cost + 12% under the VoltaGrid cost-plus
    // service order); jobs on other terms override them in Rates & Rules.
    mk: { 51100: 15, 51200: 15, 51500: 15, Equipment: 12, Materials: 12, Subcontractors: 12, Other: 12 },
    // Jobs with stored settings keep whatever prefix they saved (older jobs
    // numbered INV-); the prefix is editable under Markup & settings.
    prefix: 'SUM-', next: 1001, terms: 'Net 30',
    // Where Submit to AR emails the summary. Seth's address while the email
    // flow is being tested — switch to the real AR inbox in settings later.
    // The client representative who signs this job's daily field tickets.
    // Blank by default: it is a per-job person, and a wrong default here would
    // mail a real client. The Time Card Approval send dialog prefills from
    // these and still lets the sender override for a single shift — covering
    // the day the usual rep is out without editing the job's settings.
    signerName: '', signerEmail: '',
    // The signer MAP: who signs what. role 'Day' signs day tickets, 'Night'
    // signs night tickets, 'All' signs everything — a Day+Night packet goes
    // to the day rep, the night rep and everyone on 'All' as one envelope,
    // each on their own signature line. Empty falls back to the single
    // signer above. Each row also carries an optional day scope d ('W'
    // weekdays / 'E' weekends, absent = every day) — see SIGNER_DAYS.
    signers: [],
    // Copied on every signature request (comma separated) — the Strike side
    // that wants to see the ticket go out, e.g. the superintendent.
    signerCc: '',
    // Who on the Strike side may approve a packet before it goes to the
    // client (comma separated emails). Blank = anyone with the tab.
    approvers: '',
    // Where the Daily Force Report is emailed (comma separated). The Force
    // Report is not a signature deliverable — the Time Card Approval view
    // mails the workbook straight to these addresses, falling back to the
    // signer fields above while these are blank.
    forceTo: '', forceCc: '',
    // Per diem policy — labor provision 6 on the rate sheet: personnel
    // assigned to a project requiring a minimum of five (5) consecutive
    // workdays, and otherwise eligible for per diem, draw per diem for the
    // sixth and seventh days regardless of hours worked. Short of that
    // threshold they draw per diem only for the days actually worked.
    //   rate — dollars per per diem day (provision 5's subsistence rate)
    //   min  — days worked in the week that earn the full week
    //   full — days paid once the threshold is met
    //   ws   — week start, 1 = Monday (the app's isoWeek), 0 = Sunday
    pd: { rate: 175, min: 5, full: 7, ws: 1 },
    // Equipment fuel surcharge — see billingFuel.js. on / exFuel (keep 52300
    // fuel purchases out of the base) / q: per-job quarterly Gulf Coast
    // diesel averages that add to or override the EIA table shipped in code.
    fuel: FUEL_SETTINGS_DEFAULT,
    // Pass-through delivery/freight — see billingPassThru.js. on / mk (the %
    // pass-through lines bill at, 0 = at cost) / kw (description keywords) /
    // ct (whole cost types).
    pt: PT_SETTINGS_DEFAULT,
    // Contract billability rules — see billingContract.js. on: null follows
    // the job (on for the MEPC jobs), true/false pins it.
    ct: CONTRACT_SETTINGS_DEFAULT,
};

// ── Billability by LEMSO section ──
// The Billability Rules card's one table: every cost line on the job, under
// its LEMSO class, grouped by what decides how it bills — a contract rule
// (billingContract), the job's pass-through rules (billingPassThru), a
// never-billed cost type, or nothing (the rate sheet / class markup).
export const LEMSO_SECTIONS = ['Labor', 'Equipment', 'Materials', 'Subcontractors', 'Other'];
// Same ranges as billingPricing's bucketOf (which imports this module, so it
// can't be imported back).
export const lemsoOf = (code) => {
    const c = String(code || '');
    if (c >= '51000' && c <= '51999') return 'Labor';
    if (c >= '52000' && c <= '52999') return 'Equipment';
    if (c >= '53000' && c <= '53999') return 'Materials';
    if (c >= '54000' && c <= '54999') return 'Subcontractors';
    return 'Other';
};
const EFFECT_RANK = { nb: 0, cost: 1, warn: 2, bill: 3 };
// Burden, BYOD, truck pay and rig rent never bill whether or not the
// contract rules are on — the category they show under when the contract
// doesn't name one.
const NEVER_RULE = {
    id: 'never', effect: 'nb', label: 'Never-billed cost type — burden, BYOD, truck pay, rig rent', clause: 'Not billable cost types',
};
const NO_RULE = {
    id: 'none', effect: 'bill', none: true, label: 'Class markup', clause: '',
};
// The no-rule row names its section's markup. Labor has none — it bills
// hours × the rate sheet (see billBasis).
const noRuleLabel = (cls, mk) => {
    if (cls === 'Labor') return 'Rate sheet';
    const m = pNum((mk || DEFAULT_SETTINGS.mk)[cls]);
    return m ? `${m}% markup` : 'At cost';
};
// Rules that read as one row on the card — same effect, one reason. Pricing
// still runs each rule on its own (billingContract); the card only groups them.
const FREIGHT_ROW = { id: 'freighthaul', label: 'Freight, delivery, mob/demob and hauling — at cost' };
const NB_CT_ROW = { id: 'nbct', label: 'Not billable cost types' };
const ROW_MERGE = {
    hauling: FREIGHT_ROW, freight: FREIGHT_ROW,
    never: NB_CT_ROW, burden: NB_CT_ROW, opssupport: NB_CT_ROW, byod: NB_CT_ROW, truckpay: NB_CT_ROW, rigpay: NB_CT_ROW, corpcost: NB_CT_ROW,
};
// A rule as the card shows it — its merged row's id and label when it has one.
export const ruleRowOf = (r) => (ROW_MERGE[r.id] ? { ...r, ...ROW_MERGE[r.id] } : r);
// Per diem (51500) is one row whatever rule its cost code fell under — it
// bills at what payroll paid, not by the rule. Pass-through lines sit in a
// section of their own below the LEMSO sections.
const PD_ROW = { id: 'perdiem', effect: 'bill', label: 'Per diem — bills at what payroll paid' };
export const PT_SECTION = 'Pass-Through';
// The cost types a rule takes whole — its own `ct`, or an `any` branch that is
// nothing but a `ct` — so its row lists them before the job has cost there.
const ctOnly = (c) => !!c.ct && !c.ctp && !c.kw && !c.jde && !c.sup && !c.cc && !c.cls && c.min == null && c.max == null;
const ruleCts = (r) => {
    if (r.po) return [];
    if (!r.any) return r.ct && ctOnly(r) ? r.ct : [];
    if (r.ct || r.ctp || r.kw || r.jde || r.sup || r.cc || r.cls) return [];
    return r.any.filter(ctOnly).flatMap((c) => c.ct);
};

// How one line's bill is worked out — the table's FORMULA column. Follows
// billingPricing's priceCostRows: a line on a summary keeps what it was
// billed at (rate-billed, or its frozen markup); the rest price off the
// draft rules — pass-through at the pass-through %, contract at-cost lines
// with no markup, ST/OT and fleet / rental usage at hours × the rate sheet
// (a pass-through line never rate-bills), per diem at cost, everything else
// cost + its class markup. Equipment that carries the fuel surcharge says so.
const BASIS_LABEL = {
    nb: 'Not billed',
    rateL: 'Hours × rate sheet ST/OT rate',
    rateE: 'Hours × rate sheet equipment rate',
    norate: 'No rate sheet rate yet — bills $0',
    pd: 'Per diem at cost',
    cost: 'At cost — no markup',
};
export const billBasis = (t, r, { ptMk = 0, mk, fuel } = {}) => {
    // Off the invoice: the rule says so (unless a billable override or a
    // summary put it on), or the line was taken off for a reason of its own
    // — by hand, the cost code, a reversed invoice, rent billed on HeavyJob
    // hours. A saved contract rule the draft no longer applies doesn't count.
    const off = !!t.neverBilled || (r.effect === 'nb'
        ? !t.inv && !t.bo
        : !!t.nb && !(t.cr && t.cr.effect === 'nb'));
    if (off) return { k: 'nb', label: BASIS_LABEL.nb };
    const isPt = t.inv ? !!t.pt : !!r.pt;
    let k;
    if (t.hourly && (t.inv || !isPt)) k = t.cls === 'Labor' ? 'rateL' : 'rateE';
    else if (!t.inv && ((t.cls === 'Labor' && (t.ct === '51100' || t.ct === '51200')) || t.rentUsage)) k = 'norate';
    else {
        const m = t.inv ? pNum(t.mk)
            : isPt ? pNum(ptMk)
            : r.effect === 'cost' || t.cls === 'Labor' ? 0
            : pNum(mk ? mk[t.cls] : t.mk);
        k = m ? `${isPt ? 'pt' : 'mk'}:${m}` : t.ct === '51500' ? 'pd' : 'cost';
    }
    const label = k.startsWith('mk:') ? `Cost + ${k.slice(3)}% markup`
        : k.startsWith('pt:') ? `Cost + ${k.slice(3)}% pass-through`
        : BASIS_LABEL[k];
    // The fuel surcharge rides on the bill — a frozen adder on a summary
    // line, else the fuel settings' on / exclusions (pass-through, fuel
    // purchases, machines that burn none).
    const frozenFs = t.fs != null && t.fs !== '' && Number.isFinite(Number(t.fs));
    const fu = k !== 'norate' && (frozenFs
        ? Number(t.fs) > 0
        : !!(fuel && fuel.on) && !fuelExcluded({ ...t, nb: false, pt: isPt }, fuel));
    return fu ? { k: `${k}|f`, label: `${label} + fuel surcharge` } : { k, label };
};

// txns: billing.js's priced lines; never: its never-billed lines (burden …),
// which the pricing pass drops before they reach txns. `on` / `pt` / `mk` /
// `fuel` are the DRAFT contract toggle, pass-through rules, class markups and
// fuel settings, so the table previews an edit before Save commits it.
export const billabilityBySection = (txns, never, { on, jobKey, pt, mk, fuel } = {}) => {
    const ptN = normPt(pt);
    const secs = {};
    const sums = { nb: 0, cost: 0, warn: 0, bill: 0, none: 0 };
    let n = 0;
    // A rule's row in a section — a merged row (ROW_MERGE) cites every clause
    // its rules do.
    const groupFor = (cls, r) => {
        const m = ROW_MERGE[r.id];
        const row = ruleRowOf(r);
        const sec = secs[cls] || (secs[cls] = { cls, n: 0, cost: 0, groups: {} });
        let g = sec.groups[row.id];
        if (!g) {
            g = sec.groups[row.id] = {
                ...row, ...(row.none ? { label: noRuleLabel(cls, mk) } : {}), n: 0, cost: 0, billedCost: 0, byCt: {}, byBasis: {}, lines: [],
            };
        } else if (m && r.clause) {
            const parts = g.clause ? g.clause.split('; ') : [];
            r.clause.split('; ').forEach((p) => { if (!parts.includes(p)) parts.push(p); });
            g.clause = parts.join('; ');
        }
        return { sec, g };
    };
    const pdClause = on ? `${contractFor(jobKey).soShort} — Per diem` : 'Per diem policy';
    const add = (t, r) => {
        const cls = r.pt ? PT_SECTION : LEMSO_SECTIONS.includes(t.cls) ? t.cls : lemsoOf(t.ct);
        const c = Number(t.cost) || 0;
        const { sec, g } = groupFor(cls, String(t.ct) === '51500' ? { ...PD_ROW, clause: pdClause } : r);
        const b = billBasis(t, r, { ptMk: ptN.mk, mk, fuel });
        // The lines themselves, so a category can be opened on what's in it —
        // each with how it bills, for the drill-down's BILLABLE column.
        g.lines.push({ ...t, basis: b });
        const ctk = String(t.ct || '');
        const ct = g.byCt[ctk] || (g.byCt[ctk] = { ct: ctk, desc: t.ctDesc || '', cost: 0 });
        ct.cost += c;
        const bb = g.byBasis[b.k] || (g.byBasis[b.k] = { ...b, n: 0, cost: 0 });
        bb.n += 1; bb.cost += c;
        g.n += 1; g.cost += c; sec.n += 1; sec.cost += c; n += 1;
        if (t.inv) g.billedCost += c;
        sums[r.none ? 'none' : r.effect] += c;
    };
    (never || []).forEach((t) => {
        // Read by cost type alone — it's the cost type that never bills,
        // whatever cost code the line was charged to.
        const r = contractRule({ ...t, cc: '', ds: '' }, on, jobKey);
        add(t, r && r.effect === 'nb' ? r : NEVER_RULE);
    });
    // Same read as contractPreview: the PO-level freight rule rides in on
    // the priced line, and only a by-hand pass-through tag exempts a line
    // from a not-billable rule.
    const ruleOf = (t) => (t.cr && t.cr.po && on ? t.cr
        : contractRule(t.ptOv !== undefined ? { ...t, pt: t.ptOv === 'Y' } : t, on, jobKey));
    const rules = new Map();
    // Freight follows the item it delivered: not-billable freight on a PO
    // whose items all sit under one not-billable rule shows on that rule's
    // row. A gas monitor PO's drop-ship billed inside the monitors' $8/day
    // line, so it reads with the monitors, not with PPE. Tax rows are
    // neither item nor freight (same read as priceCostRows' freight pass).
    const poItem = {};
    (txns || []).forEach((t) => {
        if (t.neverBilled) return;
        const r = ruleOf(t);
        rules.set(t, r);
        if (!t.po || t.cls === 'Labor' || isFreightDs(t.ds) || /\btax/i.test(t.ds)) return;
        const cur = poItem[t.po];
        const nbR = r && r.effect === 'nb' ? r : null;
        poItem[t.po] = cur === undefined ? nbR : cur && nbR && cur.id === nbR.id ? cur : null;
    });
    (txns || []).forEach((t) => {
        if (t.neverBilled) return;
        let r = rules.get(t);
        const item = t.po && r && r.effect === 'nb' && !r.po && isFreightDs(t.ds) ? poItem[t.po] : null;
        if (item) r = item;
        if (r) { add(t, r); return; }
        // A line on a summary keeps the pass-through state it was billed with.
        const passes = t.inv ? !!t.pt : !t.rentUsage && isPassThru(t, ptN, t.ptOv);
        if (!passes) { add(t, NO_RULE); return; }
        const why = t.inv ? 'frozen on a summary'
            : t.ptOv === 'Y' ? 'set by hand on Transactions' : ptRuleMatch(t, ptN);
        add(t, {
            id: `pt|${why}`, effect: 'cost', pt: true,
            label: `Pass-through — ${why}`, clause: 'This job’s pass-through rules',
        });
    });
    // Every cost type a contract rule takes whole shows on its row, cost or
    // not — so a rule reads the same before the job has spent anything there.
    if (on) {
        rulesFor(jobKey).forEach((r) => ruleCts(r).forEach((ct) => {
            const { g } = groupFor(lemsoOf(ct), r);
            if (!g.byCt[ct]) g.byCt[ct] = { ct, desc: '', cost: 0 };
        }));
    }
    return {
        n,
        nbCost: sums.nb,
        costCost: sums.cost,
        warnCost: sums.warn,
        billCost: sums.bill,
        noneCost: sums.none,
        sections: [...LEMSO_SECTIONS, PT_SECTION].filter((k) => secs[k]).map((k) => ({
            ...secs[k],
            groups: Object.values(secs[k].groups)
                .map((g) => ({
                    ...g,
                    cts: Object.values(g.byCt).sort((a, b) => a.ct.localeCompare(b.ct)),
                    // How the lines bill, biggest cost first.
                    basis: Object.values(g.byBasis).sort((a, b) => Math.abs(b.cost) - Math.abs(a.cost)),
                    lines: g.lines.sort((a, b) => Math.abs(Number(b.cost) || 0) - Math.abs(Number(a.cost) || 0)),
                }))
                // In cost type order — a category that reaches into several
                // sorts on all of them, so 51300 sits above 51300 + 51350 —
                // then what comes off the invoice ahead of what bills.
                .sort((a, b) => a.cts.map((c) => c.ct).join(',').localeCompare(b.cts.map((c) => c.ct).join(','))
                    || EFFECT_RANK[a.effect] - EFFECT_RANK[b.effect]
                    || Math.abs(b.cost) - Math.abs(a.cost)),
        })),
    };
};

// ── Per diem earned per employee per week ──
// Labor cost types that prove a day was worked. Per diem (51500) itself is a
// money-only reimbursement with no hours, so it can't establish a workday —
// it only shows up below as what payroll actually paid, for comparison.
const PD_WORKED_CT = ['51100', '51200'];
const PD_COST_CT = '51500';

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Monday-start (ws 1) or Sunday-start (ws 0) week containing a YYYY-MM-DD
// date. UTC throughout — GL dates are naive dates and must not drift a day
// when the browser sits west of the server (see moment.utc use in billing.js).
export const weekStartOf = (ymd, ws = 1) => {
    const d = moment.utc(ymd, 'YYYY-MM-DD');
    if (!d.isValid()) return '';
    const shift = (d.day() - (ws ? 1 : 0) + 7) % 7;
    return d.subtract(shift, 'day').format('YYYY-MM-DD');
};

/**
 * Days of per diem a person earns for one week: the full week once they hit
 * the threshold, otherwise a day per day worked. Zero days worked earns zero
 * — the provision pays the sixth and seventh days of an assignment, not an
 * absence. Exported for the tests: this one line IS the policy.
 */
export const perDiemDays = (worked, policy) => {
    const w = Math.max(0, Math.round(pNum(worked)));
    const min = Math.max(1, Math.round(pNum(policy?.min)) || DEFAULT_SETTINGS.pd.min);
    // A blank or zero "days paid" is a mis-set policy, not a job that pays
    // nothing — fall back to the provision's seven, same as saveSettings.
    const full = Math.max(0, Math.round(pNum(policy?.full)) || DEFAULT_SETTINGS.pd.full);
    if (!w) return 0;
    return w >= min ? Math.max(full, w) : w;
};

/**
 * Roll the job's labor cost history into per diem weeks.
 *
 * A workday is any calendar day the person posted straight time or overtime
 * hours to this job (51100/51200 with hours) — one row per person per day is
 * the JDE timecard grain, but a person split across cost codes posts several,
 * so days are counted distinct. Weeks come back newest first, each with its
 * people sorted by name.
 *
 * `paid` on each person is the 51500 per diem cost payroll actually posted
 * that week, so the card can flag a week where policy and payroll disagree.
 * Exported for the tests.
 */
export const perDiemWeeks = (rows, policy) => {
    // Sunday is a real setting, so an absent one can't fall through `||` the
    // way the other policy fields do.
    const ws = policy?.ws === undefined || policy?.ws === null || policy.ws === ''
        ? DEFAULT_SETTINGS.pd.ws : (pNum(policy.ws) ? 1 : 0);
    const rate = pNum(policy?.rate);
    const weeks = {};
    const at = (date, id, name) => {
        const wk = weekStartOf(date, ws);
        if (!wk) return null;
        if (!weeks[wk]) weeks[wk] = { week: wk, people: {} };
        const p = weeks[wk].people;
        if (!p[id]) p[id] = { id, name, dates: {}, paid: 0 };
        if (!p[id].name && name) p[id].name = name;
        return p[id];
    };
    (rows || []).forEach((r) => {
        const ct = String(r?.Cost_Type_Code || '');
        if (ct !== PD_COST_CT && !PD_WORKED_CT.includes(ct)) return;
        const date = r?.GL_Date ? moment.utc(r.GL_Date).format('YYYY-MM-DD') : '';
        if (!date) return;
        const id = String(r.Staff_ID || r.Staff_First_Last_Name || '').trim();
        if (!id) return;
        const person = at(date, id, String(r.Staff_First_Last_Name || '').trim());
        if (!person) return;
        if (ct === PD_COST_CT) { person.paid += pNum(r.Job_Cost); return; }
        // A reversal can post negative hours; only a positive day is worked.
        if (pNum(r.Total_Hours) > 0) person.dates[date] = true;
    });
    return Object.values(weeks).map((w) => {
        const people = Object.values(w.people).map((p) => {
            const days = Object.keys(p.dates).length;
            const pdDays = perDiemDays(days, policy);
            return {
                id: p.id,
                name: p.name || p.id,
                days,
                dates: Object.keys(p.dates).sort(),
                pdDays,
                amount: round2(pdDays * rate),
                paid: round2(p.paid),
                full: days >= Math.max(1, Math.round(pNum(policy?.min)) || DEFAULT_SETTINGS.pd.min),
            };
        }).filter((p) => p.days || p.paid)
            .sort((a, b) => String(a.name).localeCompare(String(b.name)));
        const end = moment.utc(w.week, 'YYYY-MM-DD').add(6, 'day').format('YYYY-MM-DD');
        return {
            week: w.week,
            end,
            people,
            heads: people.length,
            days: people.reduce((s, p) => s + p.days, 0),
            pdDays: people.reduce((s, p) => s + p.pdDays, 0),
            amount: round2(people.reduce((s, p) => s + p.amount, 0)),
            paid: round2(people.reduce((s, p) => s + p.paid, 0)),
        };
    }).filter((w) => w.people.length)
        .sort((a, b) => String(b.week).localeCompare(String(a.week)));
};

// ── Merge the stored per-job overrides over the default schedule ──
// Returns { cfg, labor, equip, fuel, rules } where each list keeps default
// order, applies stored field overrides by row id, drops del rows, and
// appends custom (user-added) rows after the defaults of their section.
export const mergeRateSchedule = (userRows) => {
    let cfg = { ...DEFAULT_RATE_CONFIG };
    const ov = {};
    const customRates = [];
    const customRules = [];
    const defaultIds = new Set([
        ...DEFAULT_SCHEDULE.labor, ...DEFAULT_SCHEDULE.equip, ...DEFAULT_SCHEDULE.fuel, ...DEFAULT_SCHEDULE.rules,
    ].map((r) => r.id));
    (userRows || []).forEach((x) => {
        const fn = x?.Forecast_Field_Name;
        if (fn !== 'rate' && fn !== 'rule' && fn !== 'rateset') return;
        // Blank value + blank notes = reverted to default (reset writes these).
        if (!String(x.Notes || '').trim() && !String(x.Forecast_Value || '').trim()) return;
        if (fn === 'rateset') {
            try {
                const p = JSON.parse(x.Notes);
                cfg = { m: pNum(p.m) || cfg.m, otf: pNum(p.otf) || cfg.otf };
            } catch (e) { /* malformed blob — keep defaults */ }
            return;
        }
        let p = null;
        try { p = x.Notes ? JSON.parse(x.Notes) : null; } catch (e) { /* malformed blob — ignore */ }
        const entry = { ...(p || {}), v: x.Forecast_Value };
        if (defaultIds.has(x.Forecast_Row_ID)) {
            ov[x.Forecast_Row_ID] = entry;
        } else if (p && !p.del) {
            if (fn === 'rule' && RULE_SECTIONS.includes(p.s)) {
                customRules.push({ id: x.Forecast_Row_ID, sec: p.s, text: String(p.x || ''), custom: true });
            } else if (fn === 'rate' && ['L', 'E', 'F'].includes(p.t)) {
                customRates.push({
                    id: x.Forecast_Row_ID, t: p.t, sec: p.s || '', name: String(p.n || ''),
                    note: p.t === 'L' ? String(p.no || '') : '',
                    notes: p.t === 'E' ? String(p.no || '') : '',
                    uom: p.t === 'E' ? (p.u || 'day') : undefined,
                    fu: p.t === 'E' ? p.f !== 0 : undefined,
                    wage: p.t === 'L' ? pNum(entry.v) : undefined,
                    rate: p.t === 'E' ? pNum(entry.v) : undefined,
                    adder: p.t === 'F' ? String(entry.v ?? '') : undefined,
                    custom: true,
                });
            }
        }
    });
    const apply = (d) => {
        const o = ov[d.id];
        if (!o) return d;
        if (o.del) return null;
        return {
            ...d,
            name: o.n != null ? o.n : d.name,
            sec: o.s != null ? o.s : d.sec,
            ...(d.t === 'L' ? { note: o.no != null ? o.no : d.note, wage: o.v != null && o.v !== '' ? pNum(o.v) : d.wage } : {}),
            ...(d.t === 'E' ? {
                notes: o.no != null ? o.no : d.notes,
                uom: o.u || d.uom,
                rate: o.v != null && o.v !== '' ? pNum(o.v) : d.rate,
                // No stored f (a row saved before the flag existed) keeps the
                // schedule's own classification rather than defaulting on.
                fu: o.f != null ? o.f === 1 : d.fu,
            } : {}),
            ...(d.t === 'F' ? { adder: o.v != null && o.v !== '' ? String(o.v) : d.adder } : {}),
        };
    };
    const applyRule = (d) => {
        const o = ov[d.id];
        if (!o) return d;
        if (o.del) return null;
        return { ...d, text: o.x != null ? o.x : d.text };
    };
    return {
        cfg,
        labor: [...DEFAULT_SCHEDULE.labor.map(apply).filter(Boolean), ...customRates.filter((r) => r.t === 'L')],
        equip: [...DEFAULT_SCHEDULE.equip.map(apply).filter(Boolean), ...customRates.filter((r) => r.t === 'E')],
        fuel: [...DEFAULT_SCHEDULE.fuel.map(apply).filter(Boolean), ...customRates.filter((r) => r.t === 'F')],
        rules: [...DEFAULT_SCHEDULE.rules.map(applyRule).filter(Boolean), ...customRules],
    };
};

// ── Serialization back to Forecast_User_Data rows ──
const rateRow = (r) => {
    if (r.t === 'L') {
        return {
            fieldName: 'rate', rowId: r.id, value: String(pNum(r.wage)),
            notes: JSON.stringify({ t: 'L', s: String(r.sec).slice(0, 60), n: String(r.name).slice(0, 160), ...(r.note ? { no: String(r.note).slice(0, 200) } : {}) }),
        };
    }
    if (r.t === 'E') {
        return {
            fieldName: 'rate', rowId: r.id, value: String(pNum(r.rate)),
            notes: JSON.stringify({ t: 'E', s: String(r.sec).slice(0, 60), n: String(r.name).slice(0, 300), ...(r.notes ? { no: String(r.notes).slice(0, 300) } : {}), u: r.uom || 'day', f: r.fu ? 1 : 0 }),
        };
    }
    if (r.t === 'F') {
        return {
            fieldName: 'rate', rowId: r.id, value: String(r.adder ?? '').slice(0, 80),
            notes: JSON.stringify({ t: 'F', n: String(r.name).slice(0, 80) }),
        };
    }
    // Rules text caps at 900 chars so the JSON blob clears the 1024-char
    // Notes truncation — a chopped blob would drop the whole rule.
    return { fieldName: 'rule', rowId: r.id, value: '', notes: JSON.stringify({ s: r.sec, x: String(r.text).slice(0, 900) }) };
};

const th = { fontSize: 10, fontWeight: '700', color: '#64748b', letterSpacing: 0.4, paddingTop: 6, paddingBottom: 6, paddingRight: 8 };
const card = {
    backgroundColor: '#fff', borderRadius: 8, borderWidth: 1, borderColor: '#e2e8f0',
    paddingTop: 12, paddingBottom: 12, paddingLeft: 16, paddingRight: 16, marginBottom: 10,
};
const inputStyle = {
    fontSize: 12, borderRadius: 4, borderColor: '#cbd5e1',
    paddingTop: 4, paddingBottom: 4, paddingLeft: 6, paddingRight: 6,
};
const numStyle = { ...inputStyle, textAlign: 'right' };

const PDW = { name: 230, days: 110, pd: 110, amt: 110, paid: 110, var: 110 };
const pdCell = { fontSize: 12, color: '#1e293b', textAlign: 'right', paddingTop: 6, paddingBottom: 6, paddingRight: 8 };
// Earned minus posted. Positive means payroll paid short of the provision —
// the case this card exists to catch. Negative means it paid past it. Either
// way somebody goes and looks, so only an exact match reads as quiet.
const diffTone = (d) => {
    const v = round2(d);
    if (v > 0) return '#b45309';
    if (v < 0) return '#b91c1c';
    return '#64748b';
};
// "Aug 25 - Aug 31", with the year when the week isn't in the current one.
const weekLabel = (w) => {
    const a = moment.utc(w.week, 'YYYY-MM-DD');
    const b = moment.utc(w.end, 'YYYY-MM-DD');
    const yr = a.year() === moment.utc().year() ? '' : `, ${a.year()}`;
    return `${a.format('MMM D')} \u2013 ${b.format('MMM D')}${yr}`;
};
// The days behind a person's count, so a count that looks wrong is traceable
// to the timecards without leaving the card: "Mon Tue Wed Thu Fri".
const dayList = (dates) => (dates || []).map((d) => DOW[moment.utc(d, 'YYYY-MM-DD').day()]).join(' ');

// Gray section bar on the rate schedules — a click folds the section's rows
// away. The row count stays on the bar, and a folded section that holds
// unsaved edits says so, so a draft can't sit hidden behind a closed bar.
const SectionTitle = ({ children, shut, onPress, count, edits }) => (
    <Pressable
        onPress={onPress}
        title={shut ? 'Show this section' : 'Hide this section'}
        style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#f1f5f9', paddingTop: 5, paddingBottom: 5, paddingLeft: 8, paddingRight: 8, marginTop: 10, borderRadius: 4, cursor: 'pointer' }}
    >
        <Text style={{ fontSize: 10, color: '#64748b', width: 14 }}>{shut ? '▸' : '▾'}</Text>
        <Text style={{ fontSize: 12, fontWeight: '700', color: '#0f172a', flexGrow: 1 }}>{children}</Text>
        {shut && edits ? (
            <Text style={{ fontSize: 10, fontWeight: '700', color: '#b45309', marginRight: 10 }}>{edits} unsaved</Text>
        ) : null}
        <Text style={{ fontSize: 11, color: '#64748b' }}>{count}</Text>
    </Pressable>
);

// Title row of a card whose body folds away — the JDE maps and the equipment
// cards start folded so the sheet reads without scrolling past them. The
// buttons beside the title (children) stay reachable either way, and a folded
// card holding unsaved edits says so.
const FoldTitle = ({ title, open, onPress, edits, children }) => (
    <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
        <Pressable
            onPress={onPress}
            title={open ? 'Hide this card' : 'Show this card'}
            style={{ flexDirection: 'row', alignItems: 'center', flexGrow: 1, paddingTop: 2, paddingBottom: 2, cursor: 'pointer' }}
        >
            <Text style={{ fontSize: 11, color: '#64748b', width: 16 }}>{open ? '▾' : '▸'}</Text>
            <Text style={{ fontSize: 13, fontWeight: '700', color: '#0f172a' }}>{title}</Text>
            {!open && edits ? (
                <Text style={{ fontSize: 10, fontWeight: '700', color: '#b45309', marginLeft: 10 }}>{edits} unsaved</Text>
            ) : null}
        </Pressable>
        {children}
    </View>
);

const RemoveX = ({ onPress, disabled }) => (
    disabled ? <View style={{ width: 26 }} /> : (
        <Pressable onPress={onPress} title="Remove row" style={{ width: 26, alignItems: 'center', paddingTop: 6 }}>
            <Text style={{ fontSize: 12, color: '#b91c1c', fontWeight: '700' }}>✕</Text>
        </Pressable>
    )
);

const AddLink = ({ label, onPress, disabled }) => (
    disabled ? null : (
        <Pressable onPress={onPress} style={{ marginTop: 4, marginBottom: 2 }}>
            <Text style={{ fontSize: 11, color: '#1e40af', fontWeight: '600' }}>+ {label}</Text>
        </Pressable>
    )
);

// Labeled column inside the Markup & settings card — same grouped-divider
// pattern as the transaction view's FilterGroup. Module-level on purpose: an
// inline component type would remount its TextInputs every render (focus loss).
const SettingsGroup = ({ label, last, children }) => (
    <View style={{ marginRight: 18, marginBottom: 4, paddingRight: last ? 0 : 18, borderRightWidth: last ? 0 : 1, borderColor: '#e2e8f0' }}>
        <Text style={{ fontSize: 9, fontWeight: '700', color: '#94a3b8', letterSpacing: 0.6, marginBottom: 6 }}>{label}</Text>
        {children}
    </View>
);

const SettingsField = ({ label, children }) => (
    <View style={{ marginRight: 12, marginBottom: 6 }}>
        <Text style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>{label}</Text>
        {children}
    </View>
);

// Checkbox glyph — the same square the fuel surcharge toggles draw.
const TickBox = ({ on }) => (
    <View style={{
        width: 14, height: 14, borderRadius: 3, borderWidth: 1, borderStyle: 'solid', marginRight: 6, flexShrink: 0,
        borderColor: on ? '#0f172a' : '#94a3b8', backgroundColor: on ? '#0f172a' : '#fff',
        alignItems: 'center', justifyContent: 'center',
    }}
    >
        {on ? <Text style={{ fontSize: 9, color: '#fff', fontWeight: '700' }}>✓</Text> : null}
    </View>
);

// The equipment fuel flag as a table cell: the same tick, clickable, sized
// to the FUEL column. Editable on every row — including the schedule's own
// defaults — because whether a machine burns fuel is a per-job negotiation,
// not a rate.
const FuelTick = ({ on, onPress, disabled, title }) => (
    <Pressable
        onPress={() => !disabled && onPress()}
        title={title}
        style={{ width: 70, alignItems: 'center', paddingLeft: 6, cursor: disabled ? 'default' : 'pointer' }}
    >
        <TickBox on={on} />
    </Pressable>
);

// The transactions behind one Billability Rules category, opened from its
// row — biggest cost first, capped so a 5,000-line category doesn't bury the
// table until someone asks for the rest.
const BILL_LINE_CAP = 100;
// Does the line land on the invoice — off the basis billabilityBySection
// worked out for it (a rule, an override, the cost code, a summary).
const billsOf = (t) => !!t.basis && t.basis.k !== 'nb';
const BillLines = ({ group }) => {
    const [all, setAll] = useState(false);
    const shown = all ? group.lines : group.lines.slice(0, BILL_LINE_CAP);
    const more = group.lines.length - shown.length;
    const cell = { ...pdCell, fontSize: 11, paddingTop: 4, paddingBottom: 4, textAlign: 'left' };
    return (
        <View style={{ backgroundColor: '#f8fafc', borderBottomWidth: 1, borderStyle: 'solid', borderColor: '#e2e8f0', paddingTop: 4, paddingBottom: 8, paddingLeft: 24 }}>
            <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderStyle: 'solid', borderColor: '#e2e8f0' }}>
                <Text style={[th, { width: 70 }]}>DATE</Text>
                <Text style={[th, { width: 190 }]}>COST CODE</Text>
                <Text style={[th, { width: 170 }]}>COST TYPE</Text>
                <Text style={[th, { width: 330 }]}>EMPLOYEE / VENDOR — DESCRIPTION</Text>
                <Text style={[th, { width: 80 }]}>PO</Text>
                <Text style={[th, { width: 60, textAlign: 'right' }]}>HOURS</Text>
                <Text style={[th, { width: 100, textAlign: 'right' }]}>COST</Text>
                <Text style={[th, { width: 80, paddingLeft: 8 }]}>BILLABLE</Text>
                <Text style={[th, { width: 110, paddingLeft: 8 }]}>STATUS</Text>
            </View>
            {shown.map((t, i) => (
                <View key={t.key || i} style={{ flexDirection: 'row', borderBottomWidth: 1, borderStyle: 'solid', borderColor: '#f1f5f9' }}>
                    <Text style={[cell, { width: 70, color: '#475569' }]}>{t.date ? moment(t.date).format('M/D/YY') : ''}</Text>
                    <Text style={[cell, { width: 190, color: '#475569' }]} numberOfLines={1}>{[t.cc, t.ccDesc].filter(Boolean).join(' ')}</Text>
                    <Text style={[cell, { width: 170, color: '#475569' }]} numberOfLines={1}>{[t.ct, t.ctDesc].filter(Boolean).join(' ')}</Text>
                    <Text style={[cell, { width: 330 }]} numberOfLines={1}>{[t.who, t.eq, t.ds].filter(Boolean).join(' — ')}</Text>
                    <Text style={[cell, { width: 80, color: '#475569' }]} numberOfLines={1}>{t.po || ''}</Text>
                    <Text style={[cell, { width: 60, textAlign: 'right' }]}>{pNum(t.hrs) ? pNum(t.hrs).toLocaleString('en-US', { maximumFractionDigits: 1 }) : ''}</Text>
                    <Text style={[cell, { width: 100, textAlign: 'right' }]}>{money(t.cost)}</Text>
                    <Text style={[cell, { width: 80, paddingLeft: 8, fontWeight: '600', color: billsOf(t) ? EFFECT_TONE.bill[0] : EFFECT_TONE.nb[0] }]}>
                        {billsOf(t) ? 'Yes' : 'No'}
                    </Text>
                    <Text style={[cell, { width: 110, paddingLeft: 8, color: t.inv ? '#92400e' : '#94a3b8' }]} numberOfLines={1}>
                        {t.inv ? `On ${t.inv}` : t.nb ? 'Not billable' : t.noRate ? 'No rate' : 'Unbilled'}
                    </Text>
                </View>
            ))}
            {more > 0 || all ? (
                <View style={{ alignItems: 'flex-start', paddingTop: 2 }}>
                    <AddLink
                        label={all ? 'Show fewer' : `Show all ${group.lines.length.toLocaleString('en-US')} lines`}
                        onPress={() => setAll(!all)}
                    />
                </View>
            ) : null}
        </View>
    );
};

// Where each contract term sits in the Billability Rules table: under the
// section it governs. Markups, invoicing and a term whose section has no cost
// yet head the table.
const TERM_SECTION = {
    'Per diem': 'Labor', Overtime: 'Labor',
    'Equipment standby': 'Equipment', Fuel: 'Equipment',
    Tools: 'Materials',
    Hotels: 'Other', 'Gas monitors': 'Other', Freight: 'Other',
};
const TermRow = ({ term }) => (
    <View style={{ flexDirection: 'row', backgroundColor: '#f8fafc', borderBottomWidth: 1, borderStyle: 'solid', borderColor: '#f1f5f9' }}>
        <Text style={[pdCell, { width: 150, textAlign: 'left', paddingLeft: 8, fontSize: 11, fontWeight: '700', color: '#475569' }]}>{term.k}</Text>
        <View style={{ width: 1050, paddingTop: 6, paddingBottom: 6, paddingRight: 8 }}>
            <Text style={{ fontSize: 11, color: '#334155' }}>{term.text}</Text>
            {term.how ? <Text style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>{term.how}</Text> : null}
        </View>
    </View>
);

// A Billability Rules row's FORMULA cell: how its lines bill, one basis per
// line of text. A category that mixes several (ST/OT on the rate sheet next
// to per diem at cost) carries each basis's line count; a not-billable row
// reads as a dash. A billable row whose lines were all held off the invoice
// (by hand, the cost code …) says Not billed rather than a dash, so it can't
// pass for a row with no formula.
const BASIS_SHOWN = 3;
const BasisCell = ({ basis, effect }) => {
    const list = basis || [];
    const mixed = list.length > 1;
    const muted = (k) => k === 'nb' || k === 'norate';
    return (
        <View style={{ width: 210, paddingTop: 6, paddingBottom: 6, paddingRight: 8 }}>
            {!list.length || (effect === 'nb' && !mixed && list[0].k === 'nb') ? (
                <Text style={{ fontSize: 12, color: '#94a3b8' }}>—</Text>
            ) : list.slice(0, BASIS_SHOWN).map((b) => (
                <Text key={b.k} style={{ fontSize: 11, color: muted(b.k) ? '#94a3b8' : '#1e293b' }} numberOfLines={2}>
                    {b.label}
                    {mixed ? <Text style={{ color: '#94a3b8' }}>{`  (${b.n.toLocaleString('en-US')})`}</Text> : null}
                </Text>
            ))}
            {list.length > BASIS_SHOWN ? (
                <Text style={{ fontSize: 10, color: '#94a3b8' }}>{`+ ${list.length - BASIS_SHOWN} more`}</Text>
            ) : null}
        </View>
    );
};

// One derived-rate line: muted label left, bold value right.
const DerivedRow = ({ label, value }) => (
    <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingTop: 2, minWidth: 176 }}>
        <Text style={{ fontSize: 11, color: '#64748b' }}>{label}</Text>
        <Text style={{ fontSize: 11, color: '#0f172a', fontWeight: '600' }}>{value}</Text>
    </View>
);

let addSeq = 0;
const newId = (prefix) => `${prefix}c${Date.now().toString(36)}${(addSeq += 1)}`;

// The JDE classification catalog is company-wide and effectively static, so
// fetch it once per session and share it across job switches.
let classCatalogCache = null;
let classCatalogPromise = null;
const fetchClassCatalog = () => {
    if (classCatalogCache) return Promise.resolve(classCatalogCache);
    if (!classCatalogPromise) {
        classCatalogPromise = loadLaborClassifications({})
            .then((r) => { classCatalogCache = parseClassCatalog(r?.data); return classCatalogCache; })
            .catch(() => { classCatalogPromise = null; return []; });
    }
    return classCatalogPromise;
};

const BillingRates = ({ userRows, saveRows, versionLocked, jobKey, jobName, client, userName, jdeClasses, equipTiers, settings, costRows, txns, neverBilled, catalog, fieldMap, rentBasis, rentalUsage }) => {
    const base = useMemo(() => mergeRateSchedule(userRows), [userRows]);
    // Every active JDE classification, so the map covers classifications that
    // haven't hit this job's cost yet — not just the ones that have. Fetched
    // here unless a caller hands one in ([{ name, group }], already parsed).
    const [fetchedCatalog, setFetchedCatalog] = useState(classCatalogCache || []);
    useEffect(() => {
        if (catalog) return undefined;
        let live = true;
        fetchClassCatalog().then((c) => { if (live) setFetchedCatalog(c); });
        return () => { live = false; };
    }, [catalog]);
    const classCatalog = catalog || fetchedCatalog;
    const laborMap = useMemo(() => parseLaborMap(userRows), [userRows]);
    const equipMap = useMemo(() => parseEquipMap(userRows), [userRows]);
    // The field's own mapping, off the newest Multi Phase Field Ticket
    // workbook (loaded fresh by billing.js on every job switch) — feeds the
    // map card's suggestions so the page reflects the field's current truth.
    const fieldSug = useMemo(() => fieldSuggestions(fieldMap && fieldMap.rows), [fieldMap]);

    // Drafts: patch = edits to existing rows (by id), adds = new unsaved rows,
    // dels = removed rows, mapPatch = JDE classification map edits (by JDE
    // name), eqMapPatch = equipment tier map edits (by tier code, with the
    // tier description tagging along for the stored row). Nothing hits the
    // DB until Save changes.
    const [patch, setPatch] = useState({});
    const [dels, setDels] = useState({});
    const [adds, setAdds] = useState([]);
    const [mapPatch, setMapPatch] = useState({});
    const [eqMapPatch, setEqMapPatch] = useState({});
    const [cfgDraft, setCfgDraft] = useState(null);
    const [settingsDraft, setSettingsDraft] = useState(null);
    // The per diem week table starts hidden — the THIS JOB totals answer the
    // usual question; the per-person weeks are drill-down. Once opened it
    // shows the most recent few — a year-long job has fifty of them and
    // nobody scrolls that far to check last week.
    const [pdOpen, setPdOpen] = useState(false);
    const [pdAll, setPdAll] = useState(false);
    // 'sheet' is everything below — the rate schedule, settings and maps.
    // 'history' is the change log (billingRateLog.js), which is read-only and
    // shares nothing with the drafts, so switching tabs never discards edits.
    const [tab, setTab] = useState('sheet');
    // Opened rate schedule sections, keyed `${'L' | 'E'}|${section}` — every
    // section starts folded to its bar. Kept across job switches; the
    // sections are the same on every sheet.
    const [rateOpen, setRateOpen] = useState({});
    // Opened fold cards (FoldTitle): 'lmap', 'emap', 'rental', 'eprov'.
    const [cardOpen, setCardOpen] = useState({});
    const flipCard = (k) => setCardOpen((c) => ({ ...c, [k]: !c[k] }));

    useEffect(() => {
        setPatch({}); setDels({}); setAdds([]); setMapPatch({}); setEqMapPatch({}); setCfgDraft(null); setSettingsDraft(null); setPdOpen(false); setPdAll(false); setTab('sheet');
    }, [jobKey]);

    const edit = (id, field, val) => setPatch((p) => ({ ...p, [id]: { ...p[id], [field]: val } }));
    const editAdd = (id, field, val) => setAdds((list) => list.map((r) => (r.id === id ? { ...r, [field]: val } : r)));
    const removeRow = (row) => {
        if (row.unsaved) { setAdds((list) => list.filter((r) => r.id !== row.id)); return; }
        setDels((d) => ({ ...d, [row.id]: true }));
        setPatch((p) => { const n = { ...p }; delete n[row.id]; return n; });
    };

    const cfg = {
        m: cfgDraft ? cfgDraft.m : String(base.cfg.m),
        otf: cfgDraft ? cfgDraft.otf : String(base.cfg.otf),
    };
    const cfgNum = { m: pNum(cfg.m) || DEFAULT_RATE_CONFIG.m, otf: pNum(cfg.otf) || DEFAULT_RATE_CONFIG.otf };

    // A base row with its draft edits folded in, plus the unsaved adds.
    const viewRows = (list, t) => [
        ...list.filter((r) => !dels[r.id]).map((r) => ({ ...r, ...patch[r.id] })),
        ...adds.filter((r) => r.t === t).map((r) => ({ ...r, unsaved: true })),
    ];
    const labor = viewRows(base.labor, 'L');
    const equip = viewRows(base.equip, 'E');
    const fuel = viewRows(base.fuel, 'F');
    const rules = (sec) => [
        ...base.rules.filter((r) => r.sec === sec && !dels[r.id]).map((r) => ({ ...r, ...patch[r.id] })),
        ...adds.filter((r) => r.t === 'R' && r.sec === sec).map((r) => ({ ...r, unsaved: true })),
    ];

    const bySec = (list, secOrder) => {
        const known = secOrder.filter((s) => list.some((r) => r.sec === s));
        const extra = [...new Set(list.map((r) => r.sec).filter((s) => !secOrder.includes(s)))];
        return [...known, ...extra].map((s) => ({ sec: s, rows: list.filter((r) => r.sec === s) }));
    };
    const laborSecs = bySec(labor, LABOR_SECTIONS);
    const equipSecs = bySec(equip, EQUIP_SECTIONS);
    // Unsaved edits in one schedule section: edited or added rows still on
    // it, plus base rows deleted from it.
    const secEdits = (baseList, rows, sec) => rows.filter((r) => r.unsaved || patch[r.id]).length
        + baseList.filter((r) => r.sec === sec && dels[r.id]).length;
    const secShutOf = (t, sec) => !rateOpen[`${t}|${sec}`];
    const toggleSec = (t, sec) => setRateOpen((c) => ({ ...c, [`${t}|${sec}`]: !c[`${t}|${sec}`] }));
    const allShut = (t, secs) => secs.length > 0 && secs.every(({ sec }) => secShutOf(t, sec));
    const foldAll = (t, secs, shut) => setRateOpen((c) => {
        const n = { ...c };
        secs.forEach(({ sec }) => { n[`${t}|${sec}`] = !shut; });
        return n;
    });

    const dirty = Object.keys(patch).length + Object.keys(dels).length + adds.length
        + Object.keys(mapPatch).length + Object.keys(eqMapPatch).length + (cfgDraft ? 1 : 0);

    const discard = () => { setPatch({}); setDels({}); setAdds([]); setMapPatch({}); setEqMapPatch({}); setCfgDraft(null); };

    const save = () => {
        if (!dirty || versionLocked) return;
        const all = [...labor, ...equip, ...fuel, ...rules('labor'), ...rules('equip'), ...rules('fuel')];
        const rows = [];
        if (cfgDraft) rows.push({ fieldName: 'rateset', rowId: 'rateset', notes: JSON.stringify(cfgNum) });
        Object.keys(patch).forEach((id) => {
            const r = all.find((x) => x.id === id);
            if (r) rows.push(rateRow(r));
        });
        Object.keys(dels).forEach((id) => {
            rows.push({ fieldName: id.startsWith('ru_') ? 'rule' : 'rate', rowId: id, value: '', notes: JSON.stringify({ del: 1 }) });
        });
        // Blank unsaved adds (no name, no value, no text) just get dropped.
        adds.filter((a) => String(a.name || '').trim() || String(a.text || '').trim() || pNum(a.wage) || pNum(a.rate) || String(a.adder || '').trim())
            .forEach((a) => rows.push(rateRow(a)));
        // JDE classification map edits — '' persists as an explicit unmap.
        Object.entries(mapPatch).forEach(([jde, volta]) => rows.push({
            fieldName: 'ratemap', rowId: mapRowId(jde), value: String(volta || ''),
            notes: JSON.stringify({ j: String(jde).slice(0, 160) }),
        }));
        // Equipment tier map edits — value is the sheet row id, '' unmaps.
        Object.entries(eqMapPatch).forEach(([code, id]) => {
            const t = (equipTiers || []).find((x) => x.code === code);
            const d = tierDescr(code, t && t.descr);
            rows.push({
                fieldName: 'eqmap', rowId: equipMapRowId(code), value: String(id || ''),
                notes: JSON.stringify({ j: String(code).slice(0, 40), ...(d ? { d: d.slice(0, 80) } : {}) }),
            });
        });
        // The Version history tab's entries, written in the same round trip as
        // the rows they describe — diffed against `base` (what is stored now)
        // rather than the drafts, so a field edited back to its old value
        // doesn't log a change.
        const log = logRows(rateSaveEntries({
            base,
            next: all,
            patch,
            dels,
            adds: adds.filter((a) => rows.some((r) => r.rowId === a.id)),
            cfgAfter: cfgDraft ? cfgNum : null,
            mapPatch,
            laborMap,
            eqMapPatch,
            equipMap,
            equipName: (id) => (base.equip.find((r) => r.id === id) || {}).name,
        }), userName);
        saveRows([...rows, ...log]);
        discard();
        showToast('Rates & rules saved', 'success');
    };

    const cycleUom = (row) => {
        const order = ['hr', 'day', 'ea'];
        const next = order[(order.indexOf(row.uom || 'day') + 1) % order.length];
        if (row.unsaved) editAdd(row.id, 'uom', next); else edit(row.id, 'uom', next);
    };

    const set = (row, field) => (v) => (row.unsaved ? editAdd(row.id, field, v) : edit(row.id, field, v));

    // ── Markup & settings (billing settings row, saved separately from the
    // rate schedule; billing.js prices non-labor transactions off it — labor
    // bills hours × the sheet classification rates in billing.js's txns) ──
    const sdraft = settingsDraft || settings || DEFAULT_SETTINGS;
    // Jobs that saved settings before per diem had a policy carry no pd blob.
    const pdp = { ...DEFAULT_SETTINGS.pd, ...(sdraft.pd || {}) };
    const setPd = (k, v) => setSettingsDraft({ ...sdraft, pd: { ...pdp, [k]: v } });
    // The inputs hold strings while they are being typed; everything that
    // computes or reads back reads the numbers.
    const pdNum = {
        rate: pNum(pdp.rate),
        min: Math.max(1, Math.round(pNum(pdp.min)) || DEFAULT_SETTINGS.pd.min),
        full: Math.max(0, Math.round(pNum(pdp.full)) || DEFAULT_SETTINGS.pd.full),
        ws: pNum(pdp.ws) ? 1 : 0,
    };
    // Per diem earned per employee per week, off the job's labor cost history
    // — the same rows the Transactions view lists. Keyed on the policy fields
    // so the table previews an edit before Save settings commits it.
    const pdWeeks = useMemo(
        () => perDiemWeeks(costRows, pdp),
        [costRows, pdp.rate, pdp.min, pdp.full, pdp.ws],
    );
    const pdTotal = useMemo(() => ({
        amount: round2(pdWeeks.reduce((t, w) => t + w.amount, 0)),
        paid: round2(pdWeeks.reduce((t, w) => t + w.paid, 0)),
        days: pdWeeks.reduce((t, w) => t + w.pdDays, 0),
    }), [pdWeeks]);
    const pdShown = pdAll ? pdWeeks : pdWeeks.slice(0, 4);
    // Fuel surcharge settings draft. Quarter prices hold the typed strings
    // until Save; the adder preview reads them as numbers.
    const fdraft = { ...FUEL_SETTINGS_DEFAULT, ...(sdraft.fuel || {}), q: { ...((sdraft.fuel || {}).q || {}) } };
    const setFuel = (patch) => setSettingsDraft({ ...sdraft, fuel: { ...fdraft, ...patch } });
    // Contract billability rules draft (billingContract). on: null follows the
    // contract's own job list; the toggle pins it true or false for this job.
    const ctd = { ...CONTRACT_SETTINGS_DEFAULT, ...(sdraft.ct || {}) };
    const ctOn = contractOn({ ct: ctd }, jobKey);
    const setCt = (patch) => setSettingsDraft({ ...sdraft, ct: { ...ctd, ...patch } });
    const ctProfile = contractFor(jobKey);
    const ctTerms = useMemo(() => contractTerms(jobKey), [jobKey]);

    // Pass-through rules draft. Keywords hold the typed string until Save;
    // normPt reads either shape.
    const ptd = { ...PT_SETTINGS_DEFAULT, ...(sdraft.pt || {}) };
    const setPt = (patch) => setSettingsDraft({ ...sdraft, pt: { ...ptd, ...patch } });
    const ptNum = normPt(ptd);
    const ptKwText = Array.isArray(ptd.kw) ? ptd.kw.join(', ') : String(ptd.kw ?? '');
    const ptRuleKey = `${ptNum.on}|${ptNum.kw.join(',')}|${ptNum.ct.join(',')}`;
    // What the contract and the pass-through rules do to this job's lines, by
    // LEMSO section. Runs off the same transactions the Transactions view
    // grids (plus the never-billed cost types it leaves out), so the table is
    // what the tab actually bills — and it previews the contract toggle, the
    // pass-through rules and the markups / fuel switch before Save commits them.
    const mkKey = JSON.stringify(sdraft.mk || {});
    const fuelKey = `${!!fdraft.on}|${!!fdraft.exFuel}`;
    const bill = useMemo(
        () => billabilityBySection(txns, neverBilled, { on: ctOn, jobKey, pt: ptNum, mk: sdraft.mk, fuel: fdraft }),
        [txns, neverBilled, ctOn, jobKey, ptRuleKey, mkKey, fuelKey],
    );
    // What the rules catch on this job's cost history, rolled up by line
    // description (a freight PO line and its tax row share one), Per-line
    // overrides live on the Transactions grid and aren't reflected here.
    const ptPreview = useMemo(() => {
        const byDs = new Map();
        let n = 0;
        let cost = 0;
        (costRows || []).forEach((r) => {
            const ct = String(r?.Cost_Type_Code || '');
            if (ct < '52000' || ct >= '60000') return;
            const c = pNum(r.Job_Cost);
            if (!c) return;
            const ds = String(r.Remark_Description || '').trim();
            const why = ptRuleMatch({ cls: 'x', ct, ds }, ptNum);
            if (!why) return;
            n += 1;
            cost += c;
            const k = `${ds.toLowerCase()}|${why}`;
            const g = byDs.get(k) || { ds: ds || '(no description)', why, n: 0, cost: 0, vendors: new Set(), pos: new Set(), cts: new Set() };
            g.n += 1;
            g.cost += c;
            if (r.Supplier_Name) g.vendors.add(String(r.Supplier_Name).trim());
            if (r.PO_Number) g.pos.add(String(r.PO_Number).trim());
            g.cts.add(ct);
            byDs.set(k, g);
        });
        return {
            n,
            cost: round2(cost),
            groups: [...byDs.values()].sort((a, b) => Math.abs(b.cost) - Math.abs(a.cost)),
        };
    }, [costRows, ptRuleKey]);
    const ptOverrides = useMemo(() => (userRows || []).filter((x) => {
        if (x?.Forecast_Field_Name !== 'meta' || !x.Notes) return false;
        try { const p = JSON.parse(x.Notes); return p && (p.pt === 'Y' || p.pt === 'N'); } catch (e) { return false; }
    }).length, [userRows]);
    const [ptAll, setPtAll] = useState(false);
    const [ptOpen, setPtOpen] = useState(false);
    // Which categories are open on their lines (`${section}|${rule id}`), and
    // which LEMSO sections are folded shut.
    const [billOpen, setBillOpen] = useState({});
    const [secShut, setSecShut] = useState({});
    // The table's sections: every LEMSO class with cost.
    const billSecs = bill.sections;
    const termsBySec = useMemo(() => {
        const out = { top: [] };
        if (!ctOn) return out;
        const shown = new Set(billSecs.map((x) => x.cls));
        ctTerms.forEach((x) => {
            const at = TERM_SECTION[x.k];
            if (at && shown.has(at)) (out[at] = out[at] || []).push(x);
            else out.top.push(x);
        });
        return out;
    }, [ctOn, ctTerms, billSecs]);
    const signers = Array.isArray(sdraft.signers) ? sdraft.signers : [];
    const setSigner = (i, patch) => setSettingsDraft({
        ...sdraft, signers: signers.map((x, j) => (j === i ? { ...x, ...patch } : x)),
    });
    const saveSettings = () => {
        if (versionLocked) return;
        const clean = {
            ...sdraft,
            // Signer rows need an address to mean anything; a name alone is
            // dropped rather than saved as a line nobody can be sent.
            signers: signers
                .map((x) => ({
                    name: String(x?.name || '').trim(),
                    email: String(x?.email || '').trim(),
                    role: SIGNER_ROLES.includes(x?.role) ? x.role : 'All',
                    // Omitted at the default — the whole settings object is
                    // one 1024-char Notes cell and runs near 800 already.
                    ...(signerDays(x?.d) ? { d: signerDays(x.d) } : {}),
                }))
                .filter((x) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(x.email)),
            approvers: String(sdraft.approvers || '').trim(),
            next: Math.max(1, Math.round(pNum(sdraft.next)) || DEFAULT_SETTINGS.next),
            mk: Object.fromEntries(Object.keys(DEFAULT_SETTINGS.mk).map((k) => [k, pNum(sdraft.mk[k])])),
            // Per diem policy stores numbers, not the strings the inputs hold
            // — a "5" in min would still compare fine, but the blob is read
            // by the calculation and by anything that bills off it later.
            pd: {
                rate: pNum(pdp.rate),
                min: Math.max(1, Math.round(pNum(pdp.min)) || DEFAULT_SETTINGS.pd.min),
                full: Math.max(0, Math.round(pNum(pdp.full)) || DEFAULT_SETTINGS.pd.full),
                ws: pNum(pdp.ws) ? 1 : 0,
            },
            // Blank or zero quarter prices drop out — the EIA table (or
            // "not entered") shows through again.
            fuel: {
                on: fdraft.on !== false,
                exFuel: fdraft.exFuel !== false,
                q: Object.fromEntries(Object.entries(fdraft.q)
                    .map(([k, v]) => [k, Math.round(pNum(v) * 1000) / 1000])
                    .filter(([k, v]) => /^\d{4}-Q[1-4]$/.test(k) && v > 0)),
            },
            pt: normPt(ptd),
        };
        // Rental usage basis: a work date, 'off', or blank (auto) — which
        // drops out of the blob rather than taking room in it.
        const ruIn = String(sdraft.ru || '').trim();
        if (ruIn === 'off' || moment(ruIn, 'YYYY-MM-DD', true).isValid()) clean.ru = ruIn;
        else delete clean.ru;
        // Diffed against the merged settings the card opened on, so the log
        // records what the person changed — not the normalizing a first save
        // does to a blob that was written before a field existed.
        const log = logRows(settingsSaveEntries(settings || DEFAULT_SETTINGS, clean), userName);
        saveRows([{ fieldName: 'settings', rowId: 'settings', notes: JSON.stringify(clean) }, ...log]);
        setSettingsDraft(null);
        showToast('Billing settings saved', 'success');
    };

    // ── Excel uploads — both stage drafts only; Save changes commits them ──
    const mapFileRef = useRef(null);
    const equipFileRef = useRef(null);
    const readSheet = (file, cb) => {
        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const wb = XLSX.read(evt.target.result, { type: 'binary' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                cb(XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' }));
            } catch (e) {
                showToast('Could not read that file — save it as .xlsx or .csv and try again', 'warn');
            }
        };
        reader.readAsBinaryString(file);
    };

    // Labor map: column A = JDE classification, column B = rate sheet
    // classification (many JDE rows may point at one rate sheet class).
    // Rate-sheet names match through the same normalization the suggester
    // uses; rows whose column B isn't on this job's schedule are skipped.
    const uploadLaborMap = (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file || versionLocked) return;
        readSheet(file, (cells) => {
            const byNorm = {};
            labor.forEach((r) => { const k = normClass(r.name); if (k && !byNorm[k]) byNorm[k] = r.name; });
            const entries = {};
            const missed = [];
            cells.forEach((row, i) => {
                const jde = String(row[0] || '').trim();
                const volta = String(row[1] || '').trim();
                if (!jde || !volta) return;
                const match = byNorm[normClass(volta)];
                if (match) entries[jde] = match;
                // First row with no match is almost certainly the header.
                else if (i > 0) missed.push(volta);
            });
            if (!Object.keys(entries).length) {
                showToast('No mappings found — expected JDE classification in column A, rate sheet classification in column B', 'warn');
                return;
            }
            setMapPatch((p) => ({ ...p, ...entries }));
            const missNote = missed.length ? ` · ${missed.length} skipped (not on the rate sheet: ${[...new Set(missed)].slice(0, 3).join(', ')}${missed.length > 3 ? ', …' : ''})` : '';
            showToast(`${Object.keys(entries).length} mappings staged — review below, then Save changes${missNote}`, 'success');
        });
    };

    // Equipment rates: column A = description, B = notes / applicable models,
    // C = rate, D = UOM (hour/day/each), E = section (optional). Descriptions
    // matching an existing row update it; the rest come in as new rows under
    // their section (or "Uploaded" when none is given).
    const uploadEquipRates = (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file || versionLocked) return;
        readSheet(file, (cells) => {
            const fold = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
            const byName = {};
            equip.forEach((r) => { const k = fold(r.name); if (k && !byName[k]) byName[k] = r; });
            // Column F: Y / N, yes / no, true / false, 1 / 0 — anything else
            // (including blank) leaves the row's flag where it is.
            const fuelFlag = (v) => {
                const t = fold(v);
                if (!t) return null;
                if (['y', 'yes', 'true', '1', 'fuel', 'fueled'].includes(t)) return true;
                if (['n', 'no', 'false', '0', 'none'].includes(t)) return false;
                return null;
            };
            const uomOf = (v) => {
                const s = fold(v);
                if (!s) return null;
                if (s.includes('hour') || s === 'hr' || s === 'hrs') return 'hr';
                if (s.includes('day')) return 'day';
                if (s.includes('each') || s === 'ea') return 'ea';
                return null;
            };
            let updated = 0;
            const newAdds = [];
            cells.forEach((row) => {
                const name = String(row[0] || '').trim();
                const notes = String(row[1] || '').trim();
                const rateRaw = String(row[2] ?? '').replace(/[$,\s]/g, '');
                const uom = uomOf(row[3]);
                const sec = String(row[4] || '').trim();
                // Column F, the fuel flag, is optional: blank leaves an
                // existing row's classification alone (a sheet exported
                // before the column existed never silently reclassifies the
                // schedule) and a new row defaults to burning fuel.
                const fu = fuelFlag(row[5]);
                // Header and blank lines have no numeric rate.
                if (!name || rateRaw === '' || !Number.isFinite(parseFloat(rateRaw))) return;
                const r = byName[fold(name)];
                if (r) {
                    const put = (field, val) => (r.unsaved ? editAdd(r.id, field, val) : edit(r.id, field, val));
                    put('rate', pNum(rateRaw));
                    if (notes) put('notes', notes);
                    if (uom) put('uom', uom);
                    if (fu != null) put('fu', fu);
                    updated += 1;
                } else {
                    newAdds.push({ id: newId('rt_'), t: 'E', sec: sec || 'Uploaded', name, notes, rate: pNum(rateRaw), uom: uom || 'day', fu: fu !== false });
                }
            });
            if (!updated && !newAdds.length) {
                showToast('No rate rows found — expected description in column A and a numeric rate in column C', 'warn');
                return;
            }
            if (newAdds.length) setAdds((list) => [...list, ...newAdds]);
            showToast(`${updated} rates updated, ${newAdds.length} rows added — review, then Save changes`, 'success');
        });
    };

    // ── Map download: one workbook with both maps (a Labor Map and an
    // Equipment Map sheet), exported exactly as the cards show them —
    // unsaved edits > saved mappings > auto-suggestions. Built with exceljs
    // (same as the Summaries export) for the styled headers and the
    // rate-sheet dropdowns; the Labor Map sheet keeps JDE classification in
    // column A and rate sheet classification in column B — a dropdown of
    // this job's classifications — so a file edited in Excel re-uploads
    // as-is. The dropdown lists live on a hidden Lists sheet. Two more sheets
    // carry the coverage gaps — rate sheet rows nothing maps to — so the
    // unmapped side of the map travels with the workbook, not just the card.
    const downloadMaps = async () => {
        try {
            const rHrs = (n) => Math.round((Number(n) || 0) * 10) / 10;
            const laborNames = labor.map((r) => r.name).filter(Boolean);
            const inJobL = new Set((jdeClasses || []).map((c) => c.name));
            const mappedL = Object.keys(laborMap).filter((j) => !inJobL.has(j));
            const seenL = new Set([...inJobL, ...mappedL]);
            // The whole JDE catalog rides along — bulk-mapping the
            // classifications that haven't hit cost yet is what the workbook
            // round-trip is for.
            const laborOut = [
                ...(jdeClasses || []),
                ...mappedL.map((name) => ({ name, hrs: 0, extra: true })),
                ...classCatalog.filter((c) => !seenL.has(c.name)).map((c) => ({ name: c.name, hrs: 0, extra: true })),
            ].sort((a, b) => a.name.localeCompare(b.name)).map((r) => {
                const hasPatch = mapPatch[r.name] !== undefined;
                const hasStored = laborMap[r.name] !== undefined;
                const fieldPick = fieldSug[normClass(r.name)];
                const suggestion = !hasPatch && !hasStored
                    ? ((fieldPick && laborNames.includes(fieldPick)) ? fieldPick : suggestVoltaClass(r.name, laborNames))
                    : '';
                const value = hasPatch ? mapPatch[r.name] : hasStored ? laborMap[r.name] : suggestion;
                const lr = value ? labor.find((x) => x.name === value) : null;
                return [
                    r.name, value || '',
                    hasPatch ? 'unsaved edit' : hasStored ? 'saved' : value ? 'suggested' : 'unmapped',
                    r.extra ? '' : rHrs(r.hrs),
                    lr ? stRate(lr.wage, cfgNum) : '',
                    lr ? otRate(lr.wage, cfgNum) : '',
                ];
            });
            const equipNames = [...new Set(equip.map((r) => r.name).filter(Boolean))];
            const inJobE = new Set((equipTiers || []).map((t) => t.code));
            const hitE = new Set();
            const equipOut = [
                ...(equipTiers || []),
                ...Object.keys(equipMap).filter((c) => !inJobE.has(c)).map((code) => ({ code, descr: '', hrs: 0, extra: true })),
                ...BID_TIER_CODES.filter((c) => !inJobE.has(c) && equipMap[c] === undefined)
                    .map((code) => ({ code, descr: '', hrs: 0, extra: true })),
            ].map((r) => {
                const hasPatch = eqMapPatch[r.code] !== undefined;
                const hasStored = equipMap[r.code] !== undefined;
                const suggestion = !hasPatch && !hasStored ? suggestEquipRow(r.code, r.descr, equip) : '';
                const value = hasPatch ? eqMapPatch[r.code] : hasStored ? equipMap[r.code] : suggestion;
                const row = value ? equip.find((x) => x.id === value) : null;
                if (value) hitE.add(value);
                return [
                    r.code, tierDescr(r.code, r.descr), tierBidStatus(r.code), row ? row.name : '',
                    row ? `${money(row.rate)} ${UOM_LABELS[row.uom] || row.uom}` : `cost + ${String(pNum(sdraft.mk.Equipment))}%`,
                    hasPatch ? 'unsaved edit' : hasStored ? 'saved' : value ? 'suggested' : 'unmapped',
                    r.extra ? '' : rHrs(r.hrs),
                ];
            });
            // The other half of the map: rate sheet rows nothing points at, so
            // nothing on this job will ever bill at them. The cards report the
            // labor count on screen; both lists ride along in the workbook.
            const hitL = new Set(laborOut.map((r) => r[1]).filter(Boolean));
            const gapLabor = labor
                .filter((r) => r.name && !hitL.has(r.name))
                .map((r) => [r.name, r.sec || '', stRate(r.wage, cfgNum), otRate(r.wage, cfgNum)]);
            const gapEquip = equip
                .filter((r) => r.name && !hitE.has(r.id))
                .map((r) => [r.name, r.notes || '', r.sec || '', pNum(r.rate), UOM_LABELS[r.uom] || r.uom]);
            const ExcelJS = (await import('exceljs')).default;
            const wb = new ExcelJS.Workbook();
            wb.creator = 'Fusion Billing';
            const MONEY = '"$"#,##0.00';
            const HOURS = '#,##0.0';
            const headFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } };
            const headFont = { bold: true, size: 9, color: { argb: 'FF475569' } };
            // Frozen styled header, autofilter, per-column widths/formats.
            const sheet = (name, cols, dataRows) => {
                const ws = wb.addWorksheet(name, { views: [{ state: 'frozen', ySplit: 1 }] });
                ws.columns = cols.map((c) => ({ header: c.h, width: c.w }));
                dataRows.forEach((r) => ws.addRow(r));
                cols.forEach((c, i) => { if (c.z) ws.getColumn(i + 1).numFmt = c.z; });
                ws.getRow(1).eachCell((c) => {
                    c.font = headFont;
                    c.fill = headFill;
                    c.border = { bottom: { style: 'thin', color: { argb: 'FFCBD5E1' } } };
                });
                ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: cols.length } };
                return ws;
            };
            const wsLab = sheet('Labor Map', [
                { h: 'JDE Labor Classification', w: 34 },
                { h: 'Rate Sheet Classification', w: 42 },
                { h: 'Status', w: 13 },
                { h: 'JTD Hours', w: 11, z: HOURS },
                { h: 'ST Rate', w: 11, z: MONEY },
                { h: 'OT Rate', w: 11, z: MONEY },
            ], laborOut);
            const wsEq = sheet('Equipment Map', [
                { h: 'JDE Equipment Tier', w: 18 },
                { h: 'Tier Description', w: 32 },
                { h: 'In Bid', w: 9 },
                { h: 'Rate Sheet Equipment', w: 52 },
                { h: 'Bills At', w: 16 },
                { h: 'Status', w: 13 },
                { h: 'JTD Hours', w: 11, z: HOURS },
            ], equipOut);
            // Coverage sheets — rate sheet rows with nothing mapped to them.
            // No dropdowns here: these are read-only worklists, and the fix is
            // to point a JDE row at them back on the map sheets.
            sheet('Unmapped Labor', [
                { h: 'Rate Sheet Classification', w: 42 },
                { h: 'Section', w: 24 },
                { h: 'ST Rate', w: 11, z: MONEY },
                { h: 'OT Rate', w: 11, z: MONEY },
            ], gapLabor);
            sheet('Unmapped Equipment', [
                { h: 'Rate Sheet Equipment', w: 52 },
                { h: 'Notes / Applicable Models', w: 42 },
                { h: 'Section', w: 24 },
                { h: 'Rate', w: 12, z: MONEY },
                { h: 'UOM', w: 10 },
            ], gapEquip);
            // Dropdown source ranges on a hidden sheet (in-cell list formulae
            // cap at 255 chars, so validations must reference a range).
            const wsList = wb.addWorksheet('Lists', { state: 'hidden' });
            wsList.columns = [{ header: 'Labor Classifications', width: 42 }, { header: 'Equipment', width: 52 }];
            for (let i = 0; i < Math.max(laborNames.length, equipNames.length); i += 1) {
                wsList.addRow([laborNames[i] || '', equipNames[i] || '']);
            }
            const pick = (ws, col, count, ref) => {
                // Extra blank rows below the data stay validated so new lines
                // added in Excel get the dropdown too.
                for (let r = 2; r <= count + 26; r += 1) {
                    ws.getCell(`${col}${r}`).dataValidation = {
                        type: 'list', allowBlank: true, showErrorMessage: false, formulae: [ref],
                    };
                }
            };
            pick(wsLab, 'B', laborOut.length, `Lists!$A$2:$A$${laborNames.length + 1}`);
            pick(wsEq, 'D', equipOut.length, `Lists!$B$2:$B$${equipNames.length + 1}`);

            const buffer = await wb.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Billing_Maps_${String(jobKey || 'job').replace(/[^\w.-]+/g, '_')}_${new Date().toISOString().slice(0, 10)}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
            showToast(
                `Maps downloaded — ${laborOut.length} labor, ${equipOut.length} equipment rows`
                + ` · unmapped rate sheet rows: ${gapLabor.length} labor, ${gapEquip.length} equipment`,
                'success',
            );
        } catch (e) {
            showToast(`Excel export failed — ${e?.message || e}`, 'error');
        }
    };

    // Rendered as a plain function call (not a JSX component) — an inline
    // component type would remount its inputs on every render and drop focus
    // mid-keystroke.
    const renderRules = (sec) => (
        <View>
            {rules(sec).map((r, i) => (
                <View key={r.id} style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: 6 }}>
                    <Text style={{ fontSize: 12, fontWeight: '700', color: '#64748b', width: 24, paddingTop: 6 }}>{i + 1}.</Text>
                    <TextInput
                        multiline
                        numberOfLines={2}
                        editable={!versionLocked}
                        value={String(r.text)}
                        onChangeText={set(r, 'text')}
                        style={{ ...inputStyle, flexGrow: 1, minHeight: 34 }}
                    />
                    <RemoveX disabled={versionLocked} onPress={() => removeRow(r)} />
                </View>
            ))}
            <AddLink
                label="Add provision"
                disabled={versionLocked}
                onPress={() => setAdds((list) => [...list, { id: newId('ru_'), t: 'R', sec, text: '' }])}
            />
        </View>
    );

    return (
        <View>
            {/* Whose sheet this is. The rate schedule, markup, provisions and
                maps are all per job — and every job opens on the same default
                schedule, so without the job on the card a VoltaGrid sheet and
                an untouched one look identical. */}
            <View style={[card, { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', paddingTop: 10, paddingBottom: 10 }]}>
                <View style={{ marginRight: 20, marginBottom: 4 }}>
                    <Text style={{ fontSize: 9, fontWeight: '700', color: '#94a3b8', letterSpacing: 0.6 }}>JOB</Text>
                    <View style={{ flexDirection: 'row', alignItems: 'baseline' }}>
                        <Text style={{ fontSize: 15, fontWeight: '700', color: '#0f172a' }}>{jobKey || '—'}</Text>
                        {jobName ? <Text style={{ fontSize: 12, color: '#475569', marginLeft: 8 }}>{jobName}</Text> : null}
                    </View>
                </View>
                {/* The rate sheet is a contract document — whose contract it
                    is matters as much as which job it was negotiated for. */}
                {client ? (
                    <View style={{ marginRight: 20, marginBottom: 4 }}>
                        <Text style={{ fontSize: 9, fontWeight: '700', color: '#94a3b8', letterSpacing: 0.6 }}>CLIENT</Text>
                        <Text style={{ fontSize: 13, color: '#1e293b', paddingTop: 1 }}>{client}</Text>
                    </View>
                ) : null}
                <View style={{ flexGrow: 1 }} />
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <Chip label="Rate sheet" active={tab === 'sheet'} onPress={() => setTab('sheet')} />
                    <Chip label="Version history" active={tab === 'history'} onPress={() => setTab('history')} />
                </View>
            </View>

            {/* Save bar — only exists while there are unsaved edits (schedule,
                rules, and both map cards all stage into the same drafts). It
                sits above the tabs' content so switching to the history and
                back can't hide pending edits. */}
            {dirty > 0 && (
                <View style={[card, { flexDirection: 'row', alignItems: 'center' }]}>
                    <Text style={{ fontSize: 12, color: '#64748b', flexGrow: 1, marginRight: 12 }}>
                        {dirty === 1 ? '1 unsaved change' : `${dirty} unsaved changes`} — saves for this job only.
                    </Text>
                    <OutlineButton label="Discard changes" onPress={discard} />
                    <View style={{ width: 8 }} />
                    <Pressable
                        onPress={save}
                        style={{
                            paddingTop: 5, paddingBottom: 5, paddingLeft: 14, paddingRight: 14, borderRadius: 4,
                            backgroundColor: versionLocked ? '#cbd5e1' : '#0f172a',
                        }}
                    >
                        <Text style={{ fontSize: 12, color: '#fff', fontWeight: '600' }}>{`Save changes (${dirty})`}</Text>
                    </Pressable>
                </View>
            )}

            {tab === 'history' ? (
                <RateChangeLog userRows={userRows} jobKey={jobKey} jobName={jobName} />
            ) : (
              /* The rate sheet tab is every card below, left at its original
                 indentation — re-indenting eight hundred lines would bury the
                 change. The fragment closes just past Fuel Surcharge Provisions. */
              <>
            <View style={card}>
                <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 12 }}>
                    <View style={{ flexGrow: 1, marginRight: 12 }}>
                        <Text style={{ fontSize: 13, fontWeight: '700', color: '#0f172a' }}>Markup & settings</Text>
                        <Text style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                            Markup changes only move unbilled lines — invoiced lines keep the rate they were billed at.
                        </Text>
                    </View>
                    <OutlineButton label="Save settings" onPress={saveSettings} disabled={versionLocked || !settingsDraft} />
                </View>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                    <SettingsGroup label="LABOR BILLING">
                        <View style={{ flexDirection: 'row' }}>
                            <SettingsField label="Wage multiplier">
                                <TextInput
                                    editable={!versionLocked}
                                    value={cfg.m}
                                    onChangeText={(v) => setCfgDraft({ ...cfg, m: v })}
                                    style={{ ...numStyle, width: 84 }}
                                />
                            </SettingsField>
                            <SettingsField label="OT factor (× ST rate)">
                                <TextInput
                                    editable={!versionLocked}
                                    value={cfg.otf}
                                    onChangeText={(v) => setCfgDraft({ ...cfg, otf: v })}
                                    style={{ ...numStyle, width: 84 }}
                                />
                            </SettingsField>
                        </View>
                        <DerivedRow label="Straight time" value="hours × class ST rate" />
                        <DerivedRow label="Overtime" value="hours × class OT rate" />
                        <DerivedRow label="Per diem" value="at cost" />
                        <Text style={{ fontSize: 10, color: '#94a3b8', marginTop: 6, maxWidth: 190 }}>
                            ST rate = wage × {cfgNum.m} · OT = ST × {cfgNum.otf}, per the schedule below and the JDE map. Lines the sheet can't price show No Rate on the transactions grid. Saves with the schedule (Save changes above).
                        </Text>
                    </SettingsGroup>
                    <SettingsGroup label="COST-PLUS MARKUPS">
                        <View style={{ flexDirection: 'row', flexWrap: 'wrap', maxWidth: 220 }}>
                            {['Equipment', 'Materials', 'Subcontractors', 'Other'].map((k) => (
                                <SettingsField key={k} label={k === 'Equipment' ? 'Non-Rate-Sheet Equip %' : `${k} %`}>
                                    <TextInput
                                        editable={!versionLocked}
                                        value={String(sdraft.mk[k])}
                                        onChangeText={(v) => setSettingsDraft({ ...sdraft, mk: { ...sdraft.mk, [k]: v } })}
                                        style={{ ...numStyle, width: 84 }}
                                    />
                                </SettingsField>
                            ))}
                        </View>
                        <Text style={{ fontSize: 10, color: '#94a3b8', marginTop: 6, maxWidth: 200 }}>
                            Fleet & rental equipment bill hours × the rate sheet tier rate (rentals off their HeavyJob hours) — Equipment % only applies when a fleet tier isn't on the rate sheet.
                        </Text>
                    </SettingsGroup>
                    <SettingsGroup label="SUMMARY SETTINGS" last>
                        <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                            <SettingsField label="Invoice prefix">
                                <TextInput
                                    editable={!versionLocked}
                                    value={String(sdraft.prefix)}
                                    onChangeText={(v) => setSettingsDraft({ ...sdraft, prefix: v })}
                                    style={{ ...inputStyle, width: 84 }}
                                />
                            </SettingsField>
                            <SettingsField label="Next number">
                                <TextInput
                                    editable={!versionLocked}
                                    value={String(sdraft.next)}
                                    onChangeText={(v) => setSettingsDraft({ ...sdraft, next: v })}
                                    style={{ ...numStyle, width: 84 }}
                                />
                            </SettingsField>
                            <SettingsField label="Terms">
                                <TextInput
                                    editable={!versionLocked}
                                    value={String(sdraft.terms)}
                                    onChangeText={(v) => setSettingsDraft({ ...sdraft, terms: v })}
                                    style={{ ...inputStyle, width: 84 }}
                                />
                            </SettingsField>
                        </View>
                        {/* Field ticket signatures — the client rep the Time
                            Card Approval view sends each day's Day and Night
                            sheets to for signature. */}
                        <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                            <SettingsField label="Default signer name (used when the signer map is empty)">
                                <TextInput
                                    editable={!versionLocked}
                                    value={String(sdraft.signerName ?? '')}
                                    onChangeText={(v) => setSettingsDraft({ ...sdraft, signerName: v })}
                                    style={{ ...inputStyle, width: 180 }}
                                />
                            </SettingsField>
                            <SettingsField label="Default signer email">
                                <TextInput
                                    editable={!versionLocked}
                                    value={String(sdraft.signerEmail ?? '')}
                                    onChangeText={(v) => setSettingsDraft({ ...sdraft, signerEmail: v })}
                                    style={{ ...inputStyle, width: 276 }}
                                />
                            </SettingsField>
                        </View>
                        {/* The signer map: who signs which tickets. A row's role
                            decides which pages carry that person's signature
                            line — Day, Night, or every page. */}
                        <Text style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>
                            Signer map — who signs what, and when. Day and Night reps sign their shift&apos;s tickets;
                            All signs every ticket. The second set says which days they sign: a Weekdays signer is left
                            off a Saturday or Sunday ticket, and off a packet built only from weekend days.
                            When this list is empty the single signer above signs everything.
                        </Text>
                        {signers.map((x, i) => (
                            <View key={i} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4 }}>
                                <TextInput
                                    editable={!versionLocked}
                                    value={String(x?.name ?? '')}
                                    placeholder="Name"
                                    onChangeText={(v) => setSigner(i, { name: v })}
                                    style={{ ...inputStyle, width: 160, marginRight: 6 }}
                                />
                                <TextInput
                                    editable={!versionLocked}
                                    value={String(x?.email ?? '')}
                                    placeholder="email"
                                    onChangeText={(v) => setSigner(i, { email: v })}
                                    style={{ ...inputStyle, width: 236, marginRight: 8 }}
                                />
                                {SIGNER_ROLES.map((r) => {
                                    const on = (SIGNER_ROLES.includes(x?.role) ? x.role : 'All') === r;
                                    return (
                                        <Pressable
                                            key={r}
                                            onPress={() => !versionLocked && setSigner(i, { role: r })}
                                            style={{
                                                borderWidth: 1, borderColor: on ? '#0f172a' : '#cbd5e1', borderRadius: 4,
                                                paddingTop: 3, paddingBottom: 3, paddingLeft: 8, paddingRight: 8, marginRight: 4,
                                                backgroundColor: on ? '#0f172a' : '#fff', cursor: versionLocked ? 'default' : 'pointer',
                                            }}
                                        >
                                            <Text style={{ fontSize: 11, fontWeight: '600', color: on ? '#fff' : '#475569' }}>{r === 'All' ? 'Signs all' : r}</Text>
                                        </Pressable>
                                    );
                                })}
                                <View style={{ width: 1, height: 18, backgroundColor: '#e2e8f0', marginLeft: 6, marginRight: 6 }} />
                                {SIGNER_DAYS.map((dopt) => {
                                    const on = signerDays(x?.d) === dopt.id;
                                    return (
                                        <Pressable
                                            key={dopt.id || 'all'}
                                            onPress={() => !versionLocked && setSigner(i, { d: dopt.id })}
                                            style={{
                                                borderWidth: 1, borderColor: on ? '#0f172a' : '#cbd5e1', borderRadius: 4,
                                                paddingTop: 3, paddingBottom: 3, paddingLeft: 8, paddingRight: 8, marginRight: 4,
                                                backgroundColor: on ? '#0f172a' : '#fff', cursor: versionLocked ? 'default' : 'pointer',
                                            }}
                                        >
                                            <Text style={{ fontSize: 11, fontWeight: '600', color: on ? '#fff' : '#475569' }}>{dopt.label}</Text>
                                        </Pressable>
                                    );
                                })}
                                <RemoveX disabled={versionLocked} onPress={() => setSettingsDraft({ ...sdraft, signers: signers.filter((_, j) => j !== i) })} />
                            </View>
                        ))}
                        <AddLink label="Add signer" disabled={versionLocked} onPress={() => setSettingsDraft({ ...sdraft, signers: [...signers, { name: '', email: '', role: 'All', d: '' }] })} />
                        <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginTop: 6 }}>
                            <SettingsField label="Copy on signature requests (comma separated)">
                                <TextInput
                                    editable={!versionLocked}
                                    value={String(sdraft.signerCc ?? '')}
                                    onChangeText={(v) => setSettingsDraft({ ...sdraft, signerCc: v })}
                                    style={{ ...inputStyle, width: 276 }}
                                />
                            </SettingsField>
                            <SettingsField label="Who may approve a packet before it goes out (emails; blank = anyone)">
                                <TextInput
                                    editable={!versionLocked}
                                    value={String(sdraft.approvers ?? '')}
                                    onChangeText={(v) => setSettingsDraft({ ...sdraft, approvers: v })}
                                    style={{ ...inputStyle, width: 276 }}
                                />
                            </SettingsField>
                        </View>
                        {/* The Daily Force Report is emailed, not signed —
                            these are the client addresses the Time Card
                            Approval view mails it to. Blank falls back to the
                            signer fields above. */}
                        <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                            <SettingsField label="Force Report send to (comma separated)">
                                <TextInput
                                    editable={!versionLocked}
                                    value={String(sdraft.forceTo ?? '')}
                                    onChangeText={(v) => setSettingsDraft({ ...sdraft, forceTo: v })}
                                    style={{ ...inputStyle, width: 276 }}
                                />
                            </SettingsField>
                            <SettingsField label="Force Report copy to (comma separated)">
                                <TextInput
                                    editable={!versionLocked}
                                    value={String(sdraft.forceCc ?? '')}
                                    onChangeText={(v) => setSettingsDraft({ ...sdraft, forceCc: v })}
                                    style={{ ...inputStyle, width: 276 }}
                                />
                            </SettingsField>
                        </View>
                    </SettingsGroup>
                </View>
            </View>

            {/* Billability rules: one card for everything that decides whether
                a cost line bills and at what — the contract's billable rubric
                (the catalog and clauses live in billingContract.js), the
                job's pass-through rules (billingPassThru.js) and the cost
                types that never bill — laid out by LEMSO section. Saves on
                the billing settings row like the markups. */}
            <View style={card}>
                <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
                    <View style={{ flexGrow: 1, flexShrink: 1, flexBasis: 0, marginRight: 12, minWidth: 320 }}>
                        <Text style={{ fontSize: 13, fontWeight: '700', color: '#0f172a' }}>{`Billability Rules (VoltaGrid — ${ctProfile.name})`}</Text>
                        <Text style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                            {`The ${RUBRIC.short} (${RUBRIC.who}), read against ${ctProfile.so}, and this job's pass-through rules. `}
                            Not billable lines stay off the invoice with the rule as their reason — safety, travel,
                            onboarding, our own equipment&apos;s fuel and repairs, and the cost types that never bill:
                            burden, BYOD, truck pay and rig rent. Billable at cost lines — freight, mob/demob hauling,
                            Strike affiliates and pass-through purchases — bill with no class markup; pass-through
                            lines carry no fuel surcharge and get their own Pass-Through section on summaries, the
                            backup PDF and FTMS. Needs review lines stay billable with a CT flag for a human. Lines
                            already on a summary keep what they were billed as.
                        </Text>
                    </View>
                    <OutlineButton label="Save billability rules" onPress={saveSettings} disabled={versionLocked || !settingsDraft} />
                </View>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <SettingsGroup label="CONTRACT" last>
                        <Pressable
                            onPress={() => !versionLocked && setCt({ on: !ctOn })}
                            style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8, cursor: versionLocked ? 'default' : 'pointer' }}
                        >
                            <TickBox on={ctOn} />
                            <Text style={{ fontSize: 12, color: '#1e293b' }}>Apply the contract&apos;s billability rules on this job</Text>
                        </Pressable>
                        <Text style={{ fontSize: 10, color: '#94a3b8', maxWidth: 320 }}>
                            {ctOn
                                ? 'On — a line bills the way the first rule it matches says.'
                                : 'Off — billability follows the cost codes and the pass-through rules alone on this job.'}
                        </Text>
                    </SettingsGroup>
                </View>

                <Text style={{ fontSize: 11, color: '#475569', marginTop: 8, marginBottom: 8 }}>
                    {bill.n
                        ? `${money(bill.nbCost)} not billable · ${money(bill.costCost)} at cost · ${money(bill.warnCost)} flagged for review · ${money(bill.billCost)} billable by contract · ${money(bill.noneCost)} billable on the rate sheet or markup · ${bill.n} line${bill.n === 1 ? '' : 's'}`
                        : 'No cost on this job yet.'}
                </Text>
                {billSecs.length ? (
                    <View>
                        <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderStyle: 'solid', borderColor: '#e2e8f0' }}>
                            <Text style={[th, { width: 150, paddingLeft: 8 }]}>EFFECT</Text>
                            <Text style={[th, { width: 240 }]}>CATEGORY</Text>
                            <Text style={[th, { width: 110 }]}>COST TYPES</Text>
                            <Text style={[th, { width: 210 }]}>FORMULA</Text>
                            <Text style={[th, { width: 210 }]}>CLAUSE</Text>
                            <Text style={[th, { width: 60, textAlign: 'right' }]}>LINES</Text>
                            <Text style={[th, { width: 110, textAlign: 'right' }]}>COST</Text>
                            <Text style={[th, { width: 110, textAlign: 'right' }]}>ON SUMMARIES</Text>
                        </View>
                        {termsBySec.top.map((x) => <TermRow key={x.k} term={x} />)}
                        {billSecs.map((sec) => (
                            <View key={sec.cls}>
                                <Pressable
                                    onPress={() => setSecShut((c) => ({ ...c, [sec.cls]: !c[sec.cls] }))}
                                    title={secShut[sec.cls] ? 'Show this section' : 'Hide this section'}
                                    style={{ flexDirection: 'row', backgroundColor: '#f1f5f9', borderBottomWidth: 1, borderStyle: 'solid', borderColor: '#e2e8f0', cursor: 'pointer' }}
                                >
                                    <Text style={[pdCell, { width: 920, textAlign: 'left', paddingLeft: 8, fontSize: 11, fontWeight: '700', letterSpacing: 0.4, color: '#0f172a' }]}>
                                        <Text style={{ fontSize: 10, color: '#64748b' }}>{secShut[sec.cls] ? '▸  ' : '▾  '}</Text>
                                        {sec.cls.toUpperCase()}
                                    </Text>
                                    <Text style={[pdCell, { width: 60, fontWeight: '700' }]}>{sec.n || ''}</Text>
                                    <Text style={[pdCell, { width: 110, fontWeight: '700' }]}>{sec.n ? money(sec.cost) : ''}</Text>
                                    <View style={{ width: 110 }} />
                                </Pressable>
                                {secShut[sec.cls] ? null : (termsBySec[sec.cls] || []).map((x) => <TermRow key={x.k} term={x} />)}
                                {secShut[sec.cls] ? null : sec.groups.map((g) => {
                                    const ok = `${sec.cls}|${g.id}`;
                                    const open = !!billOpen[ok];
                                    return (
                                        <View key={g.id}>
                                            <Pressable
                                                onPress={() => setBillOpen((c) => ({ ...c, [ok]: !c[ok] }))}
                                                title={open ? 'Hide the transactions' : 'Show the transactions'}
                                                style={{ flexDirection: 'row', borderBottomWidth: 1, borderStyle: 'solid', borderColor: '#f1f5f9', cursor: 'pointer' }}
                                            >
                                                <Text style={[pdCell, { width: 150, textAlign: 'left', paddingLeft: 8, fontWeight: '700', color: g.none ? '#94a3b8' : (EFFECT_TONE[g.effect] || EFFECT_TONE.warn)[0] }]}>
                                                    <Text style={{ fontSize: 10, fontWeight: '400', color: '#64748b' }}>{open ? '▾  ' : '▸  '}</Text>
                                                    {g.pt && ptNum.mk ? `Billable at cost + ${ptNum.mk}%` : effectLabel(g.effect)}
                                                </Text>
                                                <Text style={[pdCell, { width: 240, textAlign: 'left', color: g.none ? '#64748b' : '#1e293b' }]} numberOfLines={2}>{g.label}</Text>
                                                <Text style={[pdCell, { width: 110, textAlign: 'left', color: '#475569' }]}>{g.cts.map((c) => c.ct).join(', ')}</Text>
                                                <BasisCell basis={g.basis} effect={g.effect} />
                                                <Text style={[pdCell, { width: 210, textAlign: 'left', color: '#94a3b8' }]} numberOfLines={2}>{g.clause}</Text>
                                                <Text style={[pdCell, { width: 60 }]}>{g.n}</Text>
                                                <Text style={[pdCell, { width: 110 }]}>{money(g.cost)}</Text>
                                                <Text style={[pdCell, { width: 110, color: g.billedCost ? '#92400e' : '#94a3b8' }]}>{g.billedCost ? money(g.billedCost) : '—'}</Text>
                                            </Pressable>
                                            {open ? <BillLines group={g} /> : null}
                                        </View>
                                    );
                                })}
                            </View>
                        ))}
                        <Text style={{ fontSize: 10, color: '#94a3b8', marginTop: 6, maxWidth: 820 }}>
                            A line bills the way the first rule it matches says: a not-billable rule outranks a
                            pass-through keyword (freight on an item nobody can bill is not billable either), and
                            fleet usage (52100) always bills hours × the rate sheet. FORMULA is how the row&apos;s lines
                            price — hours × the rate sheet (ST/OT labor, fleet and rental usage), cost + the class markup
                            set under Markup &amp; settings, or at cost — with each basis&apos;s line count when a row mixes
                            them; it previews unsaved markup and rule edits. ON SUMMARIES is cost that already
                            went out under a category — those lines are frozen at what they were billed as. Click a
                            row for its transactions, or a section to fold it. To move a single line, select it on Transactions and mark it billable or use Pass-through / Not
                            pass-through; the override is stamped with who and why.
                        </Text>
                    </View>
                ) : null}

                {/* Pass-through: the rules that tag lines, then what they catch. */}
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'flex-start', marginTop: 14 }}>
                    <SettingsGroup label="PASS-THROUGH" last>
                        <Pressable
                            onPress={() => !versionLocked && setPt({ on: !ptNum.on })}
                            style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8, cursor: versionLocked ? 'default' : 'pointer' }}
                        >
                            <TickBox on={ptNum.on} />
                            <Text style={{ fontSize: 12, color: '#1e293b' }}>Tag lines automatically by these rules</Text>
                        </Pressable>
                        <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                            <SettingsField label="Description contains (comma separated)">
                                <TextInput
                                    editable={!versionLocked}
                                    value={ptKwText}
                                    placeholder="freight, deliver, drop ship, 4 gas"
                                    onChangeText={(v) => setPt({ kw: v })}
                                    style={{ ...inputStyle, width: 260 }}
                                />
                            </SettingsField>
                            <SettingsField label="Pass-through markup %">
                                <TextInput
                                    editable={!versionLocked}
                                    value={String(ptd.mk ?? '')}
                                    onChangeText={(v) => setPt({ mk: v })}
                                    style={{ ...numStyle, width: 84 }}
                                />
                            </SettingsField>
                        </View>
                        <Text style={{ fontSize: 10, color: '#94a3b8', maxWidth: 520 }}>
                            Keywords match anywhere in the line description, any case — &quot;deliver&quot; catches Delivery and
                            Delivered. JDE files freight as its own line on whatever PO it came on, so the rules read the
                            description. 0% bills straight at cost.
                        </Text>
                    </SettingsGroup>
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'baseline', marginTop: 4, flexWrap: 'wrap' }}>
                    <Text style={{ fontSize: 12, fontWeight: '700', color: '#0f172a', marginRight: 8 }}>Pass-through rules on this job</Text>
                    <Text style={{ fontSize: 12, color: '#475569' }}>
                        {ptNum.on
                            ? `${ptPreview.n} line${ptPreview.n === 1 ? '' : 's'} · ${money(ptPreview.cost)} cost match the rules`
                            : 'Automatic tagging is off — only lines set by hand pass through'}
                        {ptOverrides ? ` · ${ptOverrides} line${ptOverrides === 1 ? '' : 's'} set by hand on Transactions` : ''}
                    </Text>
                </View>
                {ptNum.on && ptPreview.groups.length ? (
                    <AddLink
                        label={ptOpen ? 'Hide the matched descriptions' : `Show the ${ptPreview.groups.length} matched description${ptPreview.groups.length === 1 ? '' : 's'}`}
                        onPress={() => setPtOpen(!ptOpen)}
                    />
                ) : null}
                {ptNum.on && ptOpen && ptPreview.groups.length ? (
                    <View style={{ marginTop: 4 }}>
                        <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderStyle: 'solid', borderColor: '#e2e8f0' }}>
                            <Text style={[th, { width: 220 }]}>DESCRIPTION</Text>
                            <Text style={[th, { width: 200 }]}>VENDOR</Text>
                            <Text style={[th, { width: 150 }]}>PO</Text>
                            <Text style={[th, { width: 90 }]}>COST TYPE</Text>
                            <Text style={[th, { width: 140 }]}>MATCHED ON</Text>
                            <Text style={[th, { width: 50, textAlign: 'right' }]}>LINES</Text>
                            <Text style={[th, { width: 100, textAlign: 'right' }]}>COST</Text>
                        </View>
                        {(ptAll ? ptPreview.groups : ptPreview.groups.slice(0, 8)).map((g) => (
                            <View key={`${g.ds}|${g.why}`} style={{ flexDirection: 'row', borderBottomWidth: 1, borderStyle: 'solid', borderColor: '#f1f5f9' }}>
                                <Text style={[pdCell, { width: 220, textAlign: 'left' }]} numberOfLines={1}>{g.ds}</Text>
                                <Text style={[pdCell, { width: 200, textAlign: 'left', color: '#475569' }]} numberOfLines={1}>{[...g.vendors].join(', ') || '—'}</Text>
                                <Text style={[pdCell, { width: 150, textAlign: 'left', color: '#475569' }]} numberOfLines={1}>{[...g.pos].join(', ') || '—'}</Text>
                                <Text style={[pdCell, { width: 90, textAlign: 'left', color: '#475569' }]} numberOfLines={1}>{[...g.cts].join(', ')}</Text>
                                <Text style={[pdCell, { width: 140, textAlign: 'left', color: '#94a3b8' }]} numberOfLines={1}>{g.why}</Text>
                                <Text style={[pdCell, { width: 50 }]}>{g.n}</Text>
                                <Text style={[pdCell, { width: 100 }]}>{money(g.cost)}</Text>
                            </View>
                        ))}
                        {ptPreview.groups.length > 8 ? (
                            <AddLink
                                label={ptAll ? 'Show fewer' : `Show all ${ptPreview.groups.length} descriptions`}
                                onPress={() => setPtAll(!ptAll)}
                            />
                        ) : null}
                    </View>
                ) : null}
            </View>

            {/* Per diem: the policy, plus what it earns per person per week.
                Weeks come from the job's labor cost history; the policy saves
                on the same billing-settings row as the markups. */}
            <View style={card}>
                <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
                    <View style={{ flexGrow: 1, marginRight: 12, minWidth: 320 }}>
                        <Text style={{ fontSize: 13, fontWeight: '700', color: '#0f172a' }}>Per Diem</Text>
                        <Text style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                            Work {pdNum.min} or more days in a week and per diem pays {pdNum.full} days; work fewer and it pays only the days worked. A day counts when the person posts straight time or overtime hours to this job.
                        </Text>
                    </View>
                    {/* Same billing-settings row as the markups above, so
                        this commits a pending markup edit too — labelled for
                        the card it sits on rather than duplicating the other
                        card's button. */}
                    <OutlineButton label="Save per diem policy" onPress={saveSettings} disabled={versionLocked || !settingsDraft} />
                </View>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: 4 }}>
                    <SettingsGroup label="PER DIEM POLICY">
                        <View style={{ flexDirection: 'row', flexWrap: 'wrap', maxWidth: 360 }}>
                            <SettingsField label="Rate per day">
                                <TextInput
                                    editable={!versionLocked}
                                    value={String(pdp.rate)}
                                    onChangeText={(v) => setPd('rate', v)}
                                    style={{ ...numStyle, width: 84 }}
                                />
                            </SettingsField>
                            <SettingsField label="Days worked to earn the week">
                                <TextInput
                                    editable={!versionLocked}
                                    value={String(pdp.min)}
                                    onChangeText={(v) => setPd('min', v)}
                                    style={{ ...numStyle, width: 84 }}
                                />
                            </SettingsField>
                            <SettingsField label="Days paid at that point">
                                <TextInput
                                    editable={!versionLocked}
                                    value={String(pdp.full)}
                                    onChangeText={(v) => setPd('full', v)}
                                    style={{ ...numStyle, width: 84 }}
                                />
                            </SettingsField>
                            <SettingsField label="Week starts">
                                <Pressable
                                    onPress={() => { if (!versionLocked) setPd('ws', pdNum.ws ? 0 : 1); }}
                                    style={{ ...inputStyle, borderWidth: 1, width: 84, alignItems: 'center' }}
                                >
                                    <Text style={{ fontSize: 12, color: '#0f172a' }}>{pdNum.ws ? 'Monday' : 'Sunday'}</Text>
                                </Pressable>
                            </SettingsField>
                        </View>
                        <DerivedRow label={pdNum.min + '+ days worked'} value={pdNum.full + ' days (' + money(pdNum.full * pdNum.rate) + ')'} />
                        <DerivedRow label={'Under ' + pdNum.min + ' days'} value={'days worked \u00d7 ' + money(pdNum.rate)} />
                        <Text style={{ fontSize: 10, color: '#94a3b8', marginTop: 6, maxWidth: 360 }}>
                            Labor provision 6 on the rate sheet. Per diem is a reimbursement and bills at cost, so EARNED is what the policy owes and POSTED is what payroll actually charged to 51500.
                        </Text>
                    </SettingsGroup>
                    <SettingsGroup label="THIS JOB" last>
                        <DerivedRow label="Weeks with labor" value={String(pdWeeks.length)} />
                        <DerivedRow label="Per diem days earned" value={pdTotal.days.toLocaleString('en-US')} />
                        <DerivedRow label="Earned at policy" value={money(pdTotal.amount)} />
                        <DerivedRow label="Posted to 51500" value={money(pdTotal.paid)} />
                        <DerivedRow label="Difference" value={money(pdTotal.amount - pdTotal.paid)} />
                    </SettingsGroup>
                </View>

                {pdWeeks.length === 0 ? (
                    <Text style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                        No labor hours on this job's cost history yet — per diem weeks appear once timecards post.
                    </Text>
                ) : !pdOpen ? (
                    <AddLink
                        label={'Show weekly detail (' + pdWeeks.length + (pdWeeks.length === 1 ? ' week)' : ' weeks)')}
                        onPress={() => setPdOpen(true)}
                    />
                ) : (
                    <View style={{ marginTop: 4 }}>
                        <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderColor: '#e2e8f0' }}>
                            <Text style={[th, { width: PDW.name }]}>EMPLOYEE</Text>
                            <Text style={[th, { width: PDW.days, textAlign: 'right' }]}>DAYS WORKED</Text>
                            <Text style={[th, { width: PDW.pd, textAlign: 'right' }]}>PER DIEM DAYS</Text>
                            <Text style={[th, { width: PDW.amt, textAlign: 'right' }]}>EARNED</Text>
                            <Text style={[th, { width: PDW.paid, textAlign: 'right' }]}>POSTED</Text>
                            <Text style={[th, { width: PDW.var, textAlign: 'right' }]}>DIFFERENCE</Text>
                        </View>
                        {pdShown.map((w) => (
                            <View key={w.week}>
                                <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#f1f5f9', borderRadius: 4, marginTop: 10, paddingTop: 5, paddingBottom: 5 }}>
                                    <Text style={{ fontSize: 12, fontWeight: '700', color: '#0f172a', width: PDW.name, paddingLeft: 8 }}>
                                        {weekLabel(w)}
                                    </Text>
                                    <Text style={[pdCell, { width: PDW.days, fontWeight: '700' }]}>{w.days}</Text>
                                    <Text style={[pdCell, { width: PDW.pd, fontWeight: '700' }]}>{w.pdDays}</Text>
                                    <Text style={[pdCell, { width: PDW.amt, fontWeight: '700' }]}>{money(w.amount)}</Text>
                                    <Text style={[pdCell, { width: PDW.paid, fontWeight: '700' }]}>{money(w.paid)}</Text>
                                    <Text style={[pdCell, { width: PDW.var, fontWeight: '700', color: diffTone(w.amount - w.paid) }]}>{money(w.amount - w.paid)}</Text>
                                </View>
                                {w.people.map((p) => (
                                    <View key={w.week + '|' + p.id} style={{ flexDirection: 'row', alignItems: 'center', borderBottomWidth: 1, borderColor: '#f1f5f9', paddingTop: 3, paddingBottom: 3 }}>
                                        <View style={{ width: PDW.name, paddingLeft: 8, paddingRight: 8 }}>
                                            <Text style={{ fontSize: 12, color: '#1e293b' }}>{p.name}</Text>
                                            <Text style={{ fontSize: 10, color: '#94a3b8' }}>{dayList(p.dates)}</Text>
                                        </View>
                                        <Text style={[pdCell, { width: PDW.days }]}>{p.days}</Text>
                                        <Text style={[pdCell, { width: PDW.pd, color: p.full ? '#166534' : '#1e293b', fontWeight: p.full ? '700' : '400' }]}>{p.pdDays}</Text>
                                        <Text style={[pdCell, { width: PDW.amt }]}>{money(p.amount)}</Text>
                                        <Text style={[pdCell, { width: PDW.paid }]}>{money(p.paid)}</Text>
                                        <Text style={[pdCell, { width: PDW.var, color: diffTone(p.amount - p.paid) }]}>{money(p.amount - p.paid)}</Text>
                                    </View>
                                ))}
                            </View>
                        ))}
                        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                            {pdWeeks.length > 4 ? (
                                <AddLink
                                    label={pdAll ? 'Show only the last 4 weeks' : 'Show all ' + pdWeeks.length + ' weeks'}
                                    onPress={() => setPdAll((v) => !v)}
                                />
                            ) : null}
                            <View style={{ width: 16 }} />
                            <AddLink label="Hide weekly detail" onPress={() => setPdOpen(false)} />
                        </View>
                    </View>
                )}
            </View>

            <View style={card}>
                <View style={{ flexDirection: 'row', alignItems: 'center', maxWidth: 760 }}>
                    <Text style={{ fontSize: 13, fontWeight: '700', color: '#0f172a', flexGrow: 1 }}>Schedule of Labor Rates</Text>
                    <OutlineButton
                        label={allShut('L', laborSecs) ? 'Expand all' : 'Collapse all'}
                        onPress={() => foldAll('L', laborSecs, !allShut('L', laborSecs))}
                    />
                </View>
                <View style={{ maxWidth: 760 }}>
                    <Text style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                        Target wages, for estimating. Labor bills at each person's actual wage paid (JDE) × {String(cfgNum.m)}, overtime × {String(cfgNum.otf)} on top — the rates below are what the target would produce, never what a line bills.
                    </Text>
                    <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderColor: '#e2e8f0', marginTop: 6 }}>
                        <Text style={[th, { flexGrow: 1 }]}>CLASSIFICATION</Text>
                        <Text style={[th, { width: 130, textAlign: 'right' }]}>TARGET BASE HOURLY WAGE</Text>
                        <Text style={[th, { width: 110, textAlign: 'right' }]}>ST BILLABLE HOURLY RATE</Text>
                        <Text style={[th, { width: 110, textAlign: 'right' }]}>OVERTIME HOURLY RATE</Text>
                        <View style={{ width: 26 }} />
                    </View>
                    {laborSecs.map(({ sec, rows }) => secShutOf('L', sec) ? (
                        <SectionTitle key={sec} shut onPress={() => toggleSec('L', sec)} count={rows.length} edits={secEdits(base.labor, rows, sec)}>{sec}</SectionTitle>
                    ) : (
                        <View key={sec}>
                            <SectionTitle onPress={() => toggleSec('L', sec)} count={rows.length}>{sec}</SectionTitle>
                            {rows.map((r) => (
                                <View key={r.id} style={{ flexDirection: 'row', alignItems: 'center', borderBottomWidth: 1, borderColor: '#f1f5f9', paddingTop: 3, paddingBottom: 3 }}>
                                    <View style={{ flexGrow: 1, paddingRight: 8 }}>
                                        {r.custom || r.unsaved ? (
                                            <TextInput
                                                editable={!versionLocked}
                                                value={String(r.name)}
                                                onChangeText={set(r, 'name')}
                                                placeholder="Classification"
                                                style={{ ...inputStyle, width: '100%' }}
                                            />
                                        ) : (
                                            <Text style={{ fontSize: 12, color: '#1e293b' }}>{String(r.name)}</Text>
                                        )}
                                        {r.note ? (
                                            r.custom || r.unsaved ? (
                                                <TextInput
                                                    editable={!versionLocked}
                                                    value={String(r.note)}
                                                    onChangeText={set(r, 'note')}
                                                    style={{ ...inputStyle, width: '100%', fontSize: 10, color: '#64748b', marginTop: 2 }}
                                                />
                                            ) : (
                                                <Text style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>{String(r.note)}</Text>
                                            )
                                        ) : null}
                                    </View>
                                    {r.custom || r.unsaved ? (
                                        <TextInput
                                            editable={!versionLocked}
                                            value={String(r.wage)}
                                            onChangeText={set(r, 'wage')}
                                            style={{ ...numStyle, width: 130 }}
                                        />
                                    ) : (
                                        <Text style={{ fontSize: 12, color: '#1e293b', width: 130, textAlign: 'right' }}>{String(r.wage)}</Text>
                                    )}
                                    <Text style={{ fontSize: 12, color: '#1e293b', width: 110, textAlign: 'right' }}>{money(stRate(r.wage, cfgNum))}</Text>
                                    <Text style={{ fontSize: 12, color: '#1e293b', width: 110, textAlign: 'right' }}>{money(otRate(r.wage, cfgNum))}</Text>
                                    <RemoveX disabled={versionLocked || !(r.custom || r.unsaved)} onPress={() => removeRow(r)} />
                                </View>
                            ))}
                            <AddLink
                                label={`Add classification under ${sec}`}
                                disabled={versionLocked}
                                onPress={() => setAdds((list) => [...list, { id: newId('rt_'), t: 'L', sec, name: '', note: '', wage: '' }])}
                            />
                        </View>
                    ))}
                </View>
            </View>

            <View style={card}>
                <FoldTitle
                    title="JDE Labor Classification Map"
                    open={!!cardOpen.lmap}
                    onPress={() => flipCard('lmap')}
                    edits={Object.keys(mapPatch).length}
                >
                    <OutlineButton label="Download maps to Excel" onPress={downloadMaps} />
                    <View style={{ width: 8 }} />
                    <input ref={mapFileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={uploadLaborMap} />
                    <OutlineButton label="Upload map from Excel" onPress={() => mapFileRef.current && mapFileRef.current.click()} disabled={versionLocked} />
                </FoldTitle>
                {cardOpen.lmap ? (
                <>
                <Text style={{ fontSize: 11, color: '#64748b', marginTop: 2, marginBottom: 6 }}>
                    Maps the JDE payroll classification on each labor cost row to a rate sheet classification. Every active JDE classification is listed A–Z — the same order as the downloaded workbook — so the map can be set before the labor posts. Unsaved rows are pre-filled with the closest rate sheet match; correct any that are off and save to lock them in for this job.
                    {' '}Upload takes JDE classification in column A and rate sheet classification in column B (several JDE rows can map to the same classification).
                    {' '}Download exports both maps — labor and equipment — in one workbook, plus an Unmapped Labor and Unmapped Equipment sheet listing the rate sheet rows nothing maps to; the Labor Map sheet re-uploads as-is.
                </Text>
                {fieldMap && fieldMap.found && fieldMap.file ? (
                    <Text style={{ fontSize: 11, color: '#2563eb', marginBottom: 6 }}>
                        Suggestions follow the field's ticket workbook: {fieldMap.file.name}
                        {fieldMap.file.lastModified ? ` — updated ${new Date(fieldMap.file.lastModified).toLocaleDateString()}` : ''} (Staff List, {(fieldMap.rows || []).length} people). Refreshed on every load.
                    </Text>
                ) : null}
                <LaborClassMap
                    jdeClasses={jdeClasses || []}
                    catalog={classCatalog}
                    labor={labor}
                    stored={laborMap}
                    patch={mapPatch}
                    fieldSug={fieldSug}
                    onChange={(jde, volta) => setMapPatch((p) => ({ ...p, [jde]: volta }))}
                    rateOf={(name) => {
                        const r = labor.find((x) => x.name === name);
                        return r ? { st: money(stRate(r.wage, cfgNum)), ot: money(otRate(r.wage, cfgNum)) } : null;
                    }}
                    locked={versionLocked}
                />
                </>
                ) : null}
            </View>

            <View style={card}>
                <Text style={{ fontSize: 13, fontWeight: '700', color: '#0f172a', marginBottom: 6 }}>Labor Provisions</Text>
                {renderRules('labor')}
            </View>

            <View style={card}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <Text style={{ fontSize: 13, fontWeight: '700', color: '#0f172a', flexGrow: 1 }}>Schedule of Equipment Rates</Text>
                    <OutlineButton
                        label={allShut('E', equipSecs) ? 'Expand all' : 'Collapse all'}
                        onPress={() => foldAll('E', equipSecs, !allShut('E', equipSecs))}
                    />
                    <View style={{ width: 8 }} />
                    <input ref={equipFileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={uploadEquipRates} />
                    <OutlineButton label="Upload rates from Excel" onPress={() => equipFileRef.current && equipFileRef.current.click()} disabled={versionLocked} />
                </View>
                <Text style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                    Upload takes description in column A, notes in B, rate in C, unit (hour / day / each) in D, an optional section in E, and an optional fuel flag (Y / N) in F — matching descriptions update in place, new ones are added.
                </Text>
                <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderColor: '#e2e8f0', marginTop: 6 }}>
                    <Text style={[th, { flexGrow: 1.1 }]}>DESCRIPTION</Text>
                    <Text style={[th, { flexGrow: 1 }]}>NOTES & APPLICABLE MODELS</Text>
                    <Text style={[th, { width: 110, textAlign: 'right' }]}>RATE</Text>
                    <Text style={[th, { width: 90 }]}>UOM</Text>
                    <Text style={[th, { width: 70, textAlign: 'center' }]}>FUEL</Text>
                    <View style={{ width: 26 }} />
                </View>
                {equipSecs.map(({ sec, rows }) => secShutOf('E', sec) ? (
                    <SectionTitle key={sec} shut onPress={() => toggleSec('E', sec)} count={rows.length} edits={secEdits(base.equip, rows, sec)}>{sec}</SectionTitle>
                ) : (
                    <View key={sec}>
                        <SectionTitle onPress={() => toggleSec('E', sec)} count={rows.length}>{sec}</SectionTitle>
                        {rows.map((r) => (
                            <View key={r.id} style={{ flexDirection: 'row', alignItems: 'center', borderBottomWidth: 1, borderColor: '#f1f5f9', paddingTop: 3, paddingBottom: 3 }}>
                                <View style={{ flexGrow: 1.1, flexBasis: 0, paddingRight: 8 }}>
                                    {r.custom || r.unsaved ? (
                                        <TextInput
                                            editable={!versionLocked}
                                            value={String(r.name)}
                                            onChangeText={set(r, 'name')}
                                            placeholder="Description"
                                            style={{ ...inputStyle, width: '100%' }}
                                        />
                                    ) : (
                                        <Text style={{ fontSize: 12, color: '#1e293b' }}>{String(r.name)}</Text>
                                    )}
                                </View>
                                <View style={{ flexGrow: 1, flexBasis: 0, paddingRight: 8 }}>
                                    {r.custom || r.unsaved ? (
                                        <TextInput
                                            editable={!versionLocked}
                                            value={String(r.notes || '')}
                                            onChangeText={set(r, 'notes')}
                                            placeholder="Notes / applicable models"
                                            style={{ ...inputStyle, width: '100%', color: '#64748b' }}
                                        />
                                    ) : (
                                        <Text style={{ fontSize: 12, color: '#64748b' }}>{String(r.notes || '')}</Text>
                                    )}
                                </View>
                                {r.custom || r.unsaved ? (
                                    <TextInput
                                        editable={!versionLocked}
                                        value={String(r.rate)}
                                        onChangeText={set(r, 'rate')}
                                        style={{ ...numStyle, width: 110 }}
                                    />
                                ) : (
                                    <Text style={{ fontSize: 12, color: '#1e293b', width: 110, textAlign: 'right' }}>{String(r.rate)}</Text>
                                )}
                                {r.custom || r.unsaved ? (
                                    <Pressable
                                        onPress={() => !versionLocked && cycleUom(r)}
                                        title="Click to change the unit of measure"
                                        style={{ width: 90, paddingLeft: 8 }}
                                    >
                                        <Text style={{ fontSize: 11, color: '#1e40af', fontWeight: '600' }}>{UOM_LABELS[r.uom] || r.uom}</Text>
                                    </Pressable>
                                ) : (
                                    <Text style={{ fontSize: 11, color: '#64748b', fontWeight: '600', width: 90, paddingLeft: 8 }}>{UOM_LABELS[r.uom] || r.uom}</Text>
                                )}
                                <FuelTick
                                    on={r.fu !== false}
                                    disabled={versionLocked}
                                    onPress={() => set(r, 'fu')(r.fu === false)}
                                    title={r.fu === false
                                        ? 'Burns no fuel — left out of the fuel surcharge base'
                                        : 'Burns fuel — carries the fuel surcharge'}
                                />
                                <RemoveX disabled={versionLocked || !(r.custom || r.unsaved)} onPress={() => removeRow(r)} />
                            </View>
                        ))}
                        <AddLink
                            label={`Add equipment under ${sec}`}
                            disabled={versionLocked}
                            onPress={() => setAdds((list) => [...list, { id: newId('rt_'), t: 'E', sec, name: '', notes: '', rate: '', uom: 'day', fu: true }])}
                        />
                    </View>
                ))}
            </View>

            <View style={card}>
                <FoldTitle
                    title="JDE Equipment Tier Map"
                    open={!!cardOpen.emap}
                    onPress={() => flipCard('emap')}
                    edits={Object.keys(eqMapPatch).length}
                >
                    <OutlineButton label="Download maps to Excel" onPress={downloadMaps} />
                </FoldTitle>
                {cardOpen.emap ? (
                <>
                <Text style={{ fontSize: 11, color: '#64748b', marginTop: 2, marginBottom: 6 }}>
                    Maps the JDE equipment tier on each owned-fleet (52100) usage row and each rental unit to a rate sheet equipment row. Mapped tiers bill hours × the sheet rate
                    (day rates assume a 10-hour shift, so they bill at rate ÷ 10 per hour); unmapped fleet tiers rebill at cost + {String(pNum(sdraft.mk.Equipment))}%.
                    Rentals bill their HeavyJob card hours (JDE only carries the voucher quantity) — the tier comes off the JDE PO line, and an unmapped rental tier bills nothing until it&apos;s mapped.
                    Every tier in the bid is listed (mapping seeded from the VoltaGrid rate buildup workbook), including tiers with no cost on this job yet.
                    BID / NO BID tags are the workbook's Bid column — NO BID tiers were excluded from the bid on purpose, and untagged tiers aren't in the
                    bid workbook at all (both rebill at cost + %). Unsaved rows show the suggested match — save to lock it in for this job.
                </Text>
                <EquipTierMap
                    tiers={equipTiers || []}
                    equip={equip}
                    stored={equipMap}
                    patch={eqMapPatch}
                    onChange={(code, id) => setEqMapPatch((p) => ({ ...p, [code]: id }))}
                    fmtRate={(r) => `${money(r.rate)} ${UOM_LABELS[r.uom] || r.uom}`}
                    fallback={`cost + ${String(pNum(sdraft.mk.Equipment))}%`}
                    locked={versionLocked}
                />
                </>
                ) : null}
            </View>

            {/* Rental equipment on HeavyJob hours (billingRentalUsage): the
                on/off switch and the work date the basis starts from. */}
            <View style={card}>
                <FoldTitle title="Rental Equipment" open={!!cardOpen.rental} onPress={() => flipCard('rental')}>
                    <OutlineButton label="Save rental settings" onPress={saveSettings} disabled={versionLocked || !settingsDraft} />
                </FoldTitle>
                {cardOpen.rental ? (
                <>
                <Text style={{ fontSize: 11, color: '#64748b', marginTop: 2, marginBottom: 6 }}>
                    Rented units bill hours × their tier&apos;s rate sheet rate, the same as owned fleet — the rate applies however the equipment is
                    sourced. The hours are the unit&apos;s time on this job&apos;s HeavyJob cards (PO-&lt;po&gt;-&lt;line&gt;), one line per unit per day per cost
                    code; a unit on another job&apos;s PO bills here when its hours are on these cards. The vendor&apos;s rent voucher for a unit with
                    HeavyJob hours stays on the job as cost but defaults Not Billable (mark it billable to put it back at cost + {String(pNum(sdraft.mk.Equipment))}%).
                    A unit never set up in HeavyJob keeps billing cost + {String(pNum(sdraft.mk.Equipment))}% and is flagged. Delivery, fuel, maintenance and
                    other lines on the rental PO bill on their own rules.
                </Text>
                {(() => {
                    const ruVal = String(sdraft.ru ?? '').trim();
                    const ruOff = ruVal === 'off';
                    const mkE = String(pNum(sdraft.mk.Equipment));
                    const units = new Set((rentalUsage || []).map((u) => u.code)).size;
                    const typed = moment(ruVal, 'YYYY-MM-DD', true).isValid() ? ruVal : '';
                    const from = typed || (rentBasis && rentBasis.auto ? rentBasis.from : '');
                    return (
                        <View>
                            <Pressable
                                onPress={() => !versionLocked && setSettingsDraft({ ...sdraft, ru: ruOff ? '' : 'off' })}
                                style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8, cursor: versionLocked ? 'default' : 'pointer' }}
                            >
                                <TickBox on={!ruOff} />
                                <Text style={{ fontSize: 12, color: '#1e293b' }}>Bill rentals on their HeavyJob hours</Text>
                            </Pressable>
                            {!ruOff ? (
                                <SettingsField label="Starting work date (YYYY-MM-DD, blank = auto)">
                                    <TextInput
                                        editable={!versionLocked}
                                        value={ruVal}
                                        placeholder={rentBasis && rentBasis.auto && rentBasis.from ? rentBasis.from : 'every day'}
                                        onChangeText={(v) => setSettingsDraft({ ...sdraft, ru: v })}
                                        style={{ ...inputStyle, width: 130 }}
                                    />
                                </SettingsField>
                            ) : null}
                            <Text style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>
                                {ruOff
                                    ? `Off — rental vouchers bill cost + ${mkE}% on this job.`
                                    : rentalUsage == null && !(rentBasis && rentBasis.on)
                                    ? `HeavyJob rental hours haven't loaded — rental vouchers bill cost + ${mkE}% until they do.`
                                    : !(rentBasis && rentBasis.on)
                                    ? `No rental units (PO-…) on this job's HeavyJob cards yet — rental vouchers bill cost + ${mkE}%.`
                                    : [
                                        `${units} rental unit${units === 1 ? '' : 's'} on this job's cards.`,
                                        from
                                            ? `Usage from ${moment(from).format('M/D/YY')} bills; earlier days and vouchers dated before it stay on cost + ${mkE}%.`
                                            : 'Every usage day bills.',
                                        !typed && from
                                            ? 'Auto: the day after the last work date on a summary, since rentals through then went out on their vouchers.'
                                            : '',
                                    ].filter(Boolean).join(' ')}
                            </Text>
                        </View>
                    );
                })()}
                </>
                ) : null}
            </View>

            <View style={card}>
                <FoldTitle title="Equipment Provisions" open={!!cardOpen.eprov} onPress={() => flipCard('eprov')} />
                {cardOpen.eprov ? <View style={{ marginTop: 6 }}>{renderRules('equip')}</View> : null}
            </View>

            <View style={card}>
                <Text style={{ fontSize: 13, fontWeight: '700', color: '#0f172a' }}>Fuel Surcharge Index</Text>
                <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderColor: '#e2e8f0', marginTop: 6, width: 420 }}>
                    <Text style={[th, { width: 140 }]}>EIA GULF COAST $</Text>
                    <Text style={[th, { flexGrow: 1 }]}>EQUIPMENT % ADDER</Text>
                    <View style={{ width: 26 }} />
                </View>
                {fuel.map((r) => (
                    <View key={r.id} style={{ flexDirection: 'row', alignItems: 'center', borderBottomWidth: 1, borderColor: '#f1f5f9', paddingTop: 3, paddingBottom: 3, width: 420 }}>
                        <TextInput
                            editable={!versionLocked}
                            value={String(r.name)}
                            onChangeText={set(r, 'name')}
                            style={{ ...inputStyle, width: 132, marginRight: 8 }}
                        />
                        <TextInput
                            editable={!versionLocked}
                            value={String(r.adder)}
                            onChangeText={set(r, 'adder')}
                            style={{ ...inputStyle, flexGrow: 1 }}
                        />
                        <RemoveX disabled={versionLocked} onPress={() => removeRow(r)} />
                    </View>
                ))}
                <AddLink
                    label="Add fuel index row"
                    disabled={versionLocked}
                    onPress={() => setAdds((list) => [...list, { id: newId('rt_'), t: 'F', sec: '', name: '', adder: '' }])}
                />

                {/* How summaries bill it: the quarter's Gulf Coast diesel
                    average through the index above sets the adder for the
                    following quarter's equipment work. Saves on the billing
                    settings row, like the markups. */}
                <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 14, flexWrap: 'wrap' }}>
                    <View style={{ flexGrow: 1, marginRight: 12, minWidth: 320 }}>
                        <Text style={{ fontSize: 13, fontWeight: '700', color: '#0f172a' }}>Surcharge on Invoices</Text>
                        <Text style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                            A quarter&apos;s average U.S. EIA Gulf Coast diesel price sets the equipment adder for the next quarter&apos;s work. Invoices add it on top of the equipment subtotal.
                        </Text>
                    </View>
                    <OutlineButton label="Save fuel surcharge" onPress={saveSettings} disabled={versionLocked || !settingsDraft} />
                </View>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginTop: 8 }}>
                    {[
                        { k: 'on', label: 'Bill fuel surcharge on equipment' },
                        { k: 'exFuel', label: 'Leave 52300 fuel purchases out of the base' },
                    ].map(({ k, label }) => {
                        const on = fdraft[k] !== false;
                        return (
                            <Pressable
                                key={k}
                                onPress={() => !versionLocked && setFuel({ [k]: !on })}
                                style={{ flexDirection: 'row', alignItems: 'center', marginRight: 18, marginBottom: 4, cursor: versionLocked ? 'default' : 'pointer' }}
                            >
                                <View style={{
                                    width: 14, height: 14, borderRadius: 3, borderWidth: 1, marginRight: 6,
                                    borderColor: on ? '#0f172a' : '#94a3b8', backgroundColor: on ? '#0f172a' : '#fff',
                                    alignItems: 'center', justifyContent: 'center',
                                }}
                                >
                                    {on ? <Text style={{ fontSize: 9, color: '#fff', fontWeight: '700' }}>✓</Text> : null}
                                </View>
                                <Text style={{ fontSize: 12, color: '#1e293b' }}>{label}</Text>
                            </Pressable>
                        );
                    })}
                </View>
                <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderColor: '#e2e8f0', marginTop: 6, width: 640 }}>
                    <Text style={[th, { width: 90 }]}>PRICE QUARTER</Text>
                    <Text style={[th, { width: 110, textAlign: 'right' }]}>EIA AVG $/GAL</Text>
                    <Text style={[th, { width: 130 }]}>JOB PRICE $/GAL</Text>
                    <Text style={[th, { width: 110 }]}>BILLS WORK IN</Text>
                    <Text style={[th, { flexGrow: 1, textAlign: 'right' }]}>EQUIPMENT ADDER</Text>
                </View>
                {(() => {
                    const cur = quarterOf(moment().format('YYYY-MM-DD'));
                    // Six closed quarters plus the one in progress, newest first.
                    const qs = [0, 1, 2, 3, 4, 5, 6].map((i) => shiftQuarter(cur, -i));
                    return qs.map((q) => {
                        const eia = EIA_PADD3_QUARTERLY[q];
                        const typed = fdraft.q[q];
                        const price = typed != null && String(typed).trim() !== '' && pNum(typed) > 0 ? pNum(typed) : eia;
                        const pct = price ? adderPct(price, fuel) : null;
                        return (
                            <View key={q} style={{ flexDirection: 'row', alignItems: 'center', borderBottomWidth: 1, borderColor: '#f1f5f9', paddingTop: 3, paddingBottom: 3, width: 640 }}>
                                <Text style={{ fontSize: 12, color: '#1e293b', width: 90 }}>{q}{q === cur ? ' *' : ''}</Text>
                                <Text style={{ fontSize: 12, color: eia ? '#1e293b' : '#94a3b8', width: 110, textAlign: 'right', paddingRight: 8 }}>
                                    {eia ? `$${eia.toFixed(3)}` : '—'}
                                </Text>
                                <View style={{ width: 130 }}>
                                    <TextInput
                                        editable={!versionLocked}
                                        value={typed != null ? String(typed) : ''}
                                        placeholder={eia ? 'use EIA' : 'enter avg'}
                                        onChangeText={(v) => setFuel({ q: { ...fdraft.q, [q]: v } })}
                                        style={{ ...numStyle, width: 110 }}
                                    />
                                </View>
                                <Text style={{ fontSize: 12, color: '#475569', width: 110 }}>{quarterSpan(shiftQuarter(q, 1))}</Text>
                                <Text style={{ fontSize: 12, fontWeight: '600', color: pct == null ? '#b45309' : '#0f172a', flexGrow: 1, textAlign: 'right', paddingRight: 8 }}>
                                    {pct == null ? 'no price yet' : fmtPct(pct)}
                                </Text>
                            </View>
                        );
                    });
                })()}
                <Text style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                    * Quarter in progress. The EIA column is the average of the quarter&apos;s last 13 weekly PADD 3 on-highway diesel reports. A job price overrides it; leave blank to use EIA.
                </Text>
                <Text style={{ fontSize: 13, fontWeight: '700', color: '#0f172a', marginTop: 12, marginBottom: 6 }}>Fuel Surcharge Provisions</Text>
                {renderRules('fuel')}
            </View>
              </>
            )}
        </View>
    );
};

export default BillingRates;
